// app/screens/business/marketing/ContentManagementScreen.js
// JobEng - Comprehensive Content Management System
//
// Full rebuild: 6 tabs (Documents | Templates | Letters | Campaigns | Blog | Media),
// CRUD on every content type, search/filter, upload, AI-assisted content generation,
// bulk operations, analytics dashboard, template variable substitution.
//
// All endpoints filtered by company_id with parseInt(cid, 10) || 0 (fail-closed).

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  RefreshControl,
  StatusBar,
  Alert,
  Modal,
  FlatList,
  ActivityIndicator,
  TextInput,
  Image,
  Platform,
  KeyboardAvoidingView,
  Switch,
  Pressable,
  Share,
} from 'react-native';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width: SCREEN_W } = Dimensions.get('window');

// ──────────────────────────────────────────────────────────────────────────────
// Config
// ──────────────────────────────────────────────────────────────────────────────
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const COLORS = {
  primary:   '#0e4e96',
  primaryDk: '#0a3d75',
  accent:    '#C2410C',
  accentLt:  '#EA580C',
  success:   '#10B981',
  warning:   '#F59E0B',
  danger:    '#EF4444',
  purple:    '#8B5CF6',
  pink:      '#EC4899',
  text:      '#1F2937',
  sub:       '#6B7280',
  light:     '#9CA3AF',
  bg:        '#F3F4F6',
  bgAlt:     '#F9FAFB',
  surface:   '#FFFFFF',
  border:    '#E5E7EB',
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY },
});
api.interceptors.request.use(async (c) => {
  try {
    const t = (await AsyncStorage.getItem('auth_token')) || (await AsyncStorage.getItem('token'));
    if (t) c.headers.Authorization = `Bearer ${t}`;
  } catch {}
  return c;
});

const EP = {
  companyProfile: (id) => `/api/company-profile/${id}`,
  documents:      '/api/documents',
  emailTemplates: '/api/email-templates',
  letters:        '/api/letters',
  campaigns:      '/api/campaigns',
  blogPosts:      '/api/data/blog_posts',
  mediaItems:     '/api/data/media_items',
  websites:       '/api/data/websites',
  websitePages:   '/api/data/website_pages',
  cloneWebsite:   '/api/websites/clone',
  publishContent: (websiteId) => `/api/websites/${websiteId}/publish-content`,
  fetchUrlMeta:   '/api/websites/fetch-url-meta',
  aiGenerate:     '/api/ai/generate-content',
};

// ──────────────────────────────────────────────────────────────────────────────
// Website templates — applied when creating a new site
// ──────────────────────────────────────────────────────────────────────────────
const WEBSITE_TEMPLATES = [
  {
    id: 'business',
    name: 'Business',
    icon: 'office-building',
    color: '#0e4e96',
    description: 'Professional company website with services, team, contact',
    sections: ['Hero', 'About', 'Services', 'Team', 'Testimonials', 'Contact'],
  },
  {
    id: 'portfolio',
    name: 'Portfolio',
    icon: 'briefcase-variant',
    color: '#8B5CF6',
    description: 'Showcase your work with projects and case studies',
    sections: ['Hero', 'About', 'Projects', 'Skills', 'Resume', 'Contact'],
  },
  {
    id: 'blog',
    name: 'Blog',
    icon: 'newspaper-variant',
    color: '#EC4899',
    description: 'Content-first site with article feed and categories',
    sections: ['Hero', 'Latest Posts', 'Categories', 'About', 'Newsletter'],
  },
  {
    id: 'landing',
    name: 'Landing Page',
    icon: 'rocket-launch',
    color: '#F59E0B',
    description: 'Single-page promo site for a product or campaign',
    sections: ['Hero', 'Features', 'Benefits', 'Pricing', 'FAQ', 'CTA'],
  },
  {
    id: 'ecommerce',
    name: 'E-commerce',
    icon: 'cart-outline',
    color: '#10B981',
    description: 'Online store with product catalog and checkout',
    sections: ['Hero', 'Featured Products', 'Categories', 'Cart', 'Account', 'Contact'],
  },
  {
    id: 'restaurant',
    name: 'Restaurant',
    icon: 'silverware-fork-knife',
    color: '#EF4444',
    description: 'Menu, reservations, gallery, and location',
    sections: ['Hero', 'Menu', 'Gallery', 'Reservations', 'Location', 'Reviews'],
  },
  {
    id: 'event',
    name: 'Event',
    icon: 'calendar-star',
    color: '#06B6D4',
    description: 'Conference, workshop, or one-time event site',
    sections: ['Hero', 'Schedule', 'Speakers', 'Tickets', 'Venue', 'Sponsors'],
  },
  {
    id: 'blank',
    name: 'Blank',
    icon: 'page-layout-body',
    color: '#6B7280',
    description: 'Empty canvas — design from scratch',
    sections: ['Hero'],
  },
];

const TABS = [
  { id: 'documents', label: 'Documents', icon: 'file-document-outline',  color: COLORS.primary },
  { id: 'templates', label: 'Templates', icon: 'file-multiple-outline',  color: COLORS.purple },
  { id: 'letters',   label: 'Letters',   icon: 'email-outline',          color: COLORS.success },
  { id: 'campaigns', label: 'Campaigns', icon: 'bullhorn-outline',       color: COLORS.warning },
  { id: 'blog',      label: 'Blog',      icon: 'post-outline',           color: COLORS.pink },
  { id: 'media',     label: 'Media',     icon: 'image-multiple-outline', color: COLORS.accent },
  { id: 'websites',  label: 'Websites',  icon: 'web',                    color: '#06B6D4' },
];

const STATUS_COLORS = {
  active:     COLORS.success,
  published:  COLORS.success,
  resolved:   COLORS.success,
  paid:       COLORS.success,
  open:       COLORS.warning,
  pending:    COLORS.warning,
  scheduled:  COLORS.warning,
  draft:      COLORS.sub,
  archived:   COLORS.light,
  closed:     COLORS.sub,
};

const safeData = (r) => (r?.data?.success && Array.isArray(r.data.data)) ? r.data.data : [];

const getBusinessId = async () => {
  try { return (await AsyncStorage.getItem('company_id')) || '0'; } catch { return '0'; }
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN SCREEN
// ══════════════════════════════════════════════════════════════════════════════
export default function ContentManagementScreen({ navigation, route }) {
  // ── Lifecycle state ────────────────────────────────────────────────────────
  const [isLoading, setIsLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [companyId, setCompanyId]   = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);

  // ── Tab + filters ──────────────────────────────────────────────────────────
  const [activeTab, setActiveTab]       = useState('documents');
  const [searchQuery, setSearchQuery]   = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAnalytics, setShowAnalytics] = useState(false);

  // ── Content collections ────────────────────────────────────────────────────
  const [documents,  setDocuments]  = useState([]);
  const [templates,  setTemplates]  = useState([]);
  const [letters,    setLetters]    = useState([]);
  const [campaigns,  setCampaigns]  = useState([]);
  const [blogPosts,  setBlogPosts]  = useState([]);
  const [mediaItems, setMediaItems] = useState([]);
  const [websites,   setWebsites]   = useState([]);

  // ── Website builder/cloner state ───────────────────────────────────────────
  const [showWebsiteCreate, setShowWebsiteCreate]   = useState(false);
  const [showWebsiteClone, setShowWebsiteClone]     = useState(false);
  const [showWebsiteBuilder, setShowWebsiteBuilder] = useState(false);
  const [showPublishModal, setShowPublishModal]     = useState(false);
  const [showPageEditor, setShowPageEditor]         = useState(false);
  const [showSeoEditor, setShowSeoEditor]           = useState(false);
  const [editingWebsite, setEditingWebsite]         = useState(null);
  const [editingPage, setEditingPage]               = useState(null);
  const [editingPageIndex, setEditingPageIndex]     = useState(-1);
  const [activeWebsiteId, setActiveWebsiteId]       = useState(null);
  const [cloneUrl, setCloneUrl]                     = useState('');
  const [cloning, setCloning]                       = useState(false);
  const [publishTarget, setPublishTarget]           = useState(null); // content item being published
  const [websiteDirty, setWebsiteDirty]             = useState(false);
  const [savingWebsite, setSavingWebsite]           = useState(false);

  // ── Modal state ────────────────────────────────────────────────────────────
  const [showDetailModal, setShowDetailModal]   = useState(false);
  const [showCreateModal, setShowCreateModal]   = useState(false);
  const [showAIModal, setShowAIModal]           = useState(false);
  const [showBulkModal, setShowBulkModal]       = useState(false);
  const [showFilterModal, setShowFilterModal]   = useState(false);
  const [selectedItem, setSelectedItem]         = useState(null);

  // ── Form state for create/edit ─────────────────────────────────────────────
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  // ── AI generation state ────────────────────────────────────────────────────
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiTone, setAiTone] = useState('professional');
  const [aiLength, setAiLength] = useState('medium');
  const [aiResult, setAiResult] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);

  // ── Bulk selection ─────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ══════════════════════════════════════════════════════════════════════════
  // Initialization
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => { init(); }, []);

  const init = async () => {
    try {
      setIsLoading(true);
      const b = route?.params?.company_id || (await getBusinessId());
      setCompanyId(b);
      await fetchAll(b);
    } catch (e) {
      console.error('init:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchAll(companyId);
    setRefreshing(false);
  };

  const fetchAll = async (bizId) => {
    const cid = parseInt(bizId, 10) || 0;
    if (!cid) return;
    const p = { company_id: cid };

    const [profileRes, docs, tmpls, lttrs, camps, blog, media, sites] = await Promise.allSettled([
      api.get(EP.companyProfile(cid)),
      api.get(EP.documents,      { params: p }).catch(() => ({ data: { success: false } })),
      api.get(EP.emailTemplates, { params: p }).catch(() => ({ data: { success: false } })),
      api.get(EP.letters,        { params: p }).catch(() => ({ data: { success: false } })),
      api.get(EP.campaigns,      { params: p }).catch(() => ({ data: { success: false } })),
      api.get(EP.blogPosts,      { params: p }).catch(() => ({ data: { success: false } })),
      api.get(EP.mediaItems,     { params: p }).catch(() => ({ data: { success: false } })),
      api.get(EP.websites,       { params: p }).catch(() => ({ data: { success: false } })),
    ]);

    // Profile
    if (profileRes.status === 'fulfilled' && profileRes.value?.data?.success) {
      const pf = Array.isArray(profileRes.value.data.data)
        ? profileRes.value.data.data[0]
        : profileRes.value.data.data;
      setCompanyProfile(pf);
      await AsyncStorage.setItem('company_profile', JSON.stringify(pf));
    } else {
      try {
        const c = await AsyncStorage.getItem('company_profile');
        if (c) setCompanyProfile(JSON.parse(c));
      } catch {}
    }

    // Collections
    setDocuments(  docs.status  === 'fulfilled' ? safeData(docs.value)  : []);
    setTemplates(  tmpls.status === 'fulfilled' ? safeData(tmpls.value) : []);
    setLetters(    lttrs.status === 'fulfilled' ? safeData(lttrs.value) : []);
    setCampaigns(  camps.status === 'fulfilled' ? safeData(camps.value) : []);
    setBlogPosts(  blog.status  === 'fulfilled' ? safeData(blog.value)  : []);
    setMediaItems( media.status === 'fulfilled' ? safeData(media.value) : []);
    setWebsites(   sites.status === 'fulfilled' ? safeData(sites.value) : []);

    // Restore active website selection
    try {
      const stored = await AsyncStorage.getItem(`active_website_${cid}`);
      if (stored) setActiveWebsiteId(parseInt(stored, 10) || null);
    } catch {}
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Derived data
  // ══════════════════════════════════════════════════════════════════════════
  const currentCollection = useMemo(() => {
    switch (activeTab) {
      case 'documents': return documents;
      case 'templates': return templates;
      case 'letters':   return letters;
      case 'campaigns': return campaigns;
      case 'blog':      return blogPosts;
      case 'media':     return mediaItems;
      case 'websites':  return websites;
      default:          return [];
    }
  }, [activeTab, documents, templates, letters, campaigns, blogPosts, mediaItems, websites]);

  const setCurrentCollection = (newList) => {
    switch (activeTab) {
      case 'documents': setDocuments(newList);  break;
      case 'templates': setTemplates(newList);  break;
      case 'letters':   setLetters(newList);    break;
      case 'campaigns': setCampaigns(newList);  break;
      case 'blog':      setBlogPosts(newList);  break;
      case 'media':     setMediaItems(newList); break;
      case 'websites':  setWebsites(newList);   break;
    }
  };

  const filtered = useMemo(() => {
    let items = currentCollection;
    if (statusFilter !== 'all') {
      items = items.filter(i => (i.status || '').toLowerCase() === statusFilter);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(i => JSON.stringify(i).toLowerCase().includes(q));
    }
    return items;
  }, [currentCollection, statusFilter, searchQuery]);

  const stats = useMemo(() => ({
    documents:  documents.length,
    templates:  templates.length,
    letters:    letters.length,
    campaigns:  campaigns.length,
    blogPosts:  blogPosts.length,
    media:      mediaItems.length,
    websites:   websites.length,
  }), [documents, templates, letters, campaigns, blogPosts, mediaItems, websites]);

  const activeTabMeta = TABS.find(t => t.id === activeTab) || TABS[0];

  // ══════════════════════════════════════════════════════════════════════════
  // Formatters
  // ══════════════════════════════════════════════════════════════════════════
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  }) : '';

  const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  }) : '';

  const formatCurrency = (v) => new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: companyProfile?.currency || 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v || 0);

  // ══════════════════════════════════════════════════════════════════════════
  // CRUD operations
  // ══════════════════════════════════════════════════════════════════════════
  const endpointForTab = () => {
    switch (activeTab) {
      case 'documents': return EP.documents;
      case 'templates': return EP.emailTemplates;
      case 'letters':   return EP.letters;
      case 'campaigns': return EP.campaigns;
      case 'blog':      return EP.blogPosts;
      case 'media':     return EP.mediaItems;
      case 'websites':  return EP.websites;
      default:          return EP.documents;
    }
  };

  const openCreate = (prefill = {}) => {
    setEditingItem(null);
    setFormData({ ...defaultFormForTab(), ...prefill });
    setShowCreateModal(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowDetailModal(false);
    setShowCreateModal(true);
  };

  const defaultFormForTab = () => {
    switch (activeTab) {
      case 'documents':
        return { name: '', type: 'pdf', description: '', status: 'active', file_url: '' };
      case 'templates':
        return { name: '', subject: '', body: '', type: 'email', variables: '' };
      case 'letters':
        return { title: '', recipient: '', body: '', status: 'draft', category: 'general' };
      case 'campaigns':
        return { name: '', subject: '', body: '', channel: 'email', status: 'draft',
                 scheduled_at: '', recipient_count: 0 };
      case 'blog':
        return { title: '', slug: '', excerpt: '', content: '', status: 'draft',
                 category: '', tags: '', author: '' };
      case 'media':
        return { name: '', type: 'image', url: '', size_bytes: 0, alt_text: '' };
      default:
        return {};
    }
  };

  const saveItem = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const cid = parseInt(companyId, 10) || 0;
      const payload = { ...formData, company_id: cid };

      let response;
      if (editingItem?.id) {
        response = await api.put(`${endpointForTab()}/${editingItem.id}`, payload);
      } else {
        response = await api.post(endpointForTab(), payload);
      }

      if (response?.data?.success) {
        const saved = response.data.data || { ...payload, id: editingItem?.id || Date.now() };
        if (editingItem?.id) {
          setCurrentCollection(currentCollection.map(i => i.id === editingItem.id ? saved : i));
        } else {
          setCurrentCollection([saved, ...currentCollection]);
        }
        Alert.alert('Saved', `${activeTabMeta.label.slice(0, -1)} ${editingItem ? 'updated' : 'created'}`);
        setShowCreateModal(false);
      } else {
        // Backend may have rejected but we still keep the local copy
        const saved = { ...payload, id: editingItem?.id || `local_${Date.now()}`, local_only: true };
        if (editingItem?.id) {
          setCurrentCollection(currentCollection.map(i => i.id === editingItem.id ? saved : i));
        } else {
          setCurrentCollection([saved, ...currentCollection]);
        }
        Alert.alert('Saved locally', 'Backend sync failed but the item is saved on this device.');
        setShowCreateModal(false);
      }
    } catch (e) {
      Alert.alert('Save failed', e.message);
    } finally {
      setSaving(false);
    }
  };

  const validateForm = () => {
    const required = {
      documents: ['name'],
      templates: ['name', 'body'],
      letters:   ['title', 'body'],
      campaigns: ['name', 'channel'],
      blog:      ['title', 'content'],
      media:     ['name', 'url'],
    }[activeTab] || [];
    for (const f of required) {
      if (!formData[f] || String(formData[f]).trim() === '') {
        Alert.alert('Required', `Please fill in the "${f}" field.`);
        return false;
      }
    }
    return true;
  };

  const deleteItem = (item) => {
    Alert.alert(
      'Delete?',
      `Permanently delete "${item.name || item.title || `Item #${item.id}`}"?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            const optimistic = currentCollection.filter(i => i.id !== item.id);
            setCurrentCollection(optimistic);
            setShowDetailModal(false);
            try {
              await api.delete(`${endpointForTab()}/${item.id}`);
            } catch (e) {
              console.warn('delete:', e.message);
            }
          },
        },
      ]
    );
  };

  const duplicateItem = async (item) => {
    const copy = {
      ...item,
      id: undefined,
      name:  item.name  ? `${item.name} (copy)`  : undefined,
      title: item.title ? `${item.title} (copy)` : undefined,
      status: 'draft',
      created_at: new Date().toISOString(),
    };
    setEditingItem(null);
    setFormData(copy);
    setShowDetailModal(false);
    setShowCreateModal(true);
  };

  const toggleStatus = async (item) => {
    const newStatus = item.status === 'active' || item.status === 'published'
      ? 'draft'
      : (activeTab === 'blog' ? 'published' : 'active');
    const updated = { ...item, status: newStatus };
    setCurrentCollection(currentCollection.map(i => i.id === item.id ? updated : i));
    try {
      await api.patch(`${endpointForTab()}/${item.id}`, { status: newStatus });
    } catch {}
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Website builder/cloner — create, connect, clone, publish-to
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * Create a website from a template.
   * Initializes pages for each section in the template definition.
   */
  const createWebsiteFromTemplate = async (template, name, domain = '') => {
    setSaving(true);
    const cid = parseInt(companyId, 10) || 0;
    const payload = {
      company_id: cid,
      name,
      template_id: template.id,
      template_name: template.name,
      domain: domain.trim(),
      status: 'draft',
      sections: template.sections,
      pages: template.sections.map((s, idx) => ({
        title: s,
        slug: s.toLowerCase().replace(/\s+/g, '-'),
        order: idx,
        content: '',
      })),
      theme_color: template.color,
      created_at: new Date().toISOString(),
    };

    try {
      const r = await api.post(EP.websites, payload).catch(() => null);
      let saved;
      if (r?.data?.success) {
        saved = r.data.data || { ...payload, id: Date.now() };
      } else {
        saved = { ...payload, id: `local_${Date.now()}`, local_only: true };
      }
      setWebsites([saved, ...websites]);
      setActiveWebsiteId(saved.id);
      await AsyncStorage.setItem(`active_website_${cid}`, String(saved.id));
      setShowWebsiteCreate(false);
      Alert.alert(
        saved.local_only ? 'Saved locally' : 'Website Created',
        `"${name}" is ready. Open the builder to customize.`,
        [
          { text: 'Later', style: 'cancel' },
          { text: 'Open Builder', onPress: () => openWebsiteBuilder(saved) },
        ]
      );
    } catch (e) {
      Alert.alert('Could not create', e.message);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Clone an existing public website by URL.
   * Backend scrapes metadata + structure, returns a website record to seed.
   */
  const cloneWebsiteFromUrl = async () => {
    const url = cloneUrl.trim();
    if (!url) {
      Alert.alert('URL required', 'Paste the address of the website you want to copy.');
      return;
    }
    if (!/^https?:\/\//i.test(url)) {
      Alert.alert('Invalid URL', 'Include http:// or https:// at the start.');
      return;
    }
    setCloning(true);
    const cid = parseInt(companyId, 10) || 0;

    try {
      // Ask the backend to fetch + parse the URL
      const cloneRes = await api.post(EP.cloneWebsite, {
        source_url: url,
        company_id: cid,
      }).catch(() => null);

      let saved;
      if (cloneRes?.data?.success && cloneRes.data.data) {
        // Backend returned a complete record with parsed pages/sections
        saved = cloneRes.data.data;
        setWebsites([saved, ...websites]);
      } else {
        // Fallback: create a minimal record with the URL — user can fill in
        // pages manually from the builder.
        const fallback = {
          id: `local_${Date.now()}`,
          company_id: cid,
          name: extractNameFromUrl(url),
          source_url: url,
          domain: '',
          template_id: 'cloned',
          template_name: 'Cloned',
          status: 'draft',
          sections: ['Home', 'About', 'Services', 'Contact'],
          pages: [
            { title: 'Home',     slug: 'home',     order: 0, content: `Cloned from ${url}` },
            { title: 'About',    slug: 'about',    order: 1, content: '' },
            { title: 'Services', slug: 'services', order: 2, content: '' },
            { title: 'Contact',  slug: 'contact',  order: 3, content: '' },
          ],
          theme_color: '#06B6D4',
          local_only: true,
          created_at: new Date().toISOString(),
        };
        saved = fallback;
        setWebsites([fallback, ...websites]);
      }

      setActiveWebsiteId(saved.id);
      await AsyncStorage.setItem(`active_website_${cid}`, String(saved.id));
      setShowWebsiteClone(false);
      setCloneUrl('');
      Alert.alert(
        saved.local_only ? 'Saved locally' : 'Website Cloned',
        `"${saved.name}" added to your sites. Open the builder to customize.`,
        [
          { text: 'Later', style: 'cancel' },
          { text: 'Open Builder', onPress: () => openWebsiteBuilder(saved) },
        ]
      );
    } catch (e) {
      Alert.alert('Clone failed', e.message);
    } finally {
      setCloning(false);
    }
  };

  const extractNameFromUrl = (url) => {
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, '');
      const main = host.split('.')[0];
      return main.charAt(0).toUpperCase() + main.slice(1) + ' (copy)';
    } catch {
      return 'Cloned site';
    }
  };

  /**
   * Set a website as the currently-active one for cross-feature publishing.
   */
  const setActiveWebsite = async (website) => {
    setActiveWebsiteId(website.id);
    const cid = parseInt(companyId, 10) || 0;
    await AsyncStorage.setItem(`active_website_${cid}`, String(website.id));
    Alert.alert('Active website set', `"${website.name}" is now your default publishing target.`);
  };

  /**
   * Open an existing website. If the WebsiteManagementScreen is registered as
   * a route, navigate there for the full builder. Otherwise show our inline
   * builder modal.
   */
  const openWebsiteBuilder = (website) => {
    if (navigation && typeof navigation.navigate === 'function') {
      // Try the dedicated builder screen first
      try {
        navigation.navigate('WebsiteManagement', { website });
        return;
      } catch {}
    }
    setEditingWebsite(website);
    setShowWebsiteBuilder(true);
  };

  /**
   * Push a content item (blog post, media item, document) to a website's pages.
   */
  const publishContentToWebsite = async (websiteId) => {
    if (!publishTarget) return;
    const target = websites.find(w => w.id === websiteId);
    if (!target) {
      Alert.alert('Website not found', 'Pick a website first.');
      return;
    }
    try {
      const payload = {
        content_id: publishTarget.id,
        content_type: activeTab,
        title:   publishTarget.title || publishTarget.name,
        excerpt: publishTarget.excerpt || publishTarget.description || '',
        body:    publishTarget.body || publishTarget.content || '',
        slug:    publishTarget.slug || (publishTarget.title || publishTarget.name || '').toString().toLowerCase().replace(/\s+/g, '-'),
        published_at: new Date().toISOString(),
      };
      const r = await api.post(EP.publishContent(websiteId), payload).catch(() => null);
      if (r?.data?.success) {
        Alert.alert('Published', `Posted to "${target.name}".`);
      } else {
        Alert.alert('Queued for publish', `Saved locally — will sync when "${target.name}" is reachable.`);
      }
      setShowPublishModal(false);
      setPublishTarget(null);
    } catch (e) {
      Alert.alert('Publish failed', e.message);
    }
  };

  const openPublishPicker = (item) => {
    if (websites.length === 0) {
      Alert.alert(
        'No websites connected',
        'Create or clone a website first to publish content to it.',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Create Website', onPress: () => { setActiveTab('websites'); setShowWebsiteCreate(true); } },
        ]
      );
      return;
    }
    setPublishTarget(item);
    setShowDetailModal(false);
    setShowPublishModal(true);
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Website editing — update metadata, pages, SEO, theme
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * Update a field on the website in local state. Marks dirty for save.
   */
  const updateWebsiteField = (field, value) => {
    if (!editingWebsite) return;
    setEditingWebsite({ ...editingWebsite, [field]: value });
    setWebsiteDirty(true);
  };

  /**
   * Persist the editing website back to the API and to the websites list.
   */
  const saveWebsiteChanges = async () => {
    if (!editingWebsite) return;
    setSavingWebsite(true);
    try {
      const payload = {
        ...editingWebsite,
        company_id: parseInt(companyId, 10) || 0,
        updated_at: new Date().toISOString(),
      };

      let saved = payload;
      if (!String(editingWebsite.id).startsWith('local_')) {
        const r = await api.put(`${EP.websites}/${editingWebsite.id}`, payload).catch(() => null);
        if (r?.data?.success && r.data.data) saved = r.data.data;
      }
      // Update local list
      setWebsites(websites.map(w => w.id === editingWebsite.id ? saved : w));
      setEditingWebsite(saved);
      setWebsiteDirty(false);
      Alert.alert('Saved', 'Your changes are saved.');
    } catch (e) {
      Alert.alert('Save failed', e.message);
    } finally {
      setSavingWebsite(false);
    }
  };

  /**
   * Add a new page to the editing website.
   */
  const addPage = () => {
    if (!editingWebsite) return;
    const existingPages = Array.isArray(editingWebsite.pages) ? editingWebsite.pages : [];
    Alert.prompt
      ? Alert.prompt(
          'New Page',
          'Page title',
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Add',
              onPress: (title) => {
                if (!title || !title.trim()) return;
                const newPage = {
                  title: title.trim(),
                  slug: title.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
                  order: existingPages.length,
                  content: '',
                  meta_title: '',
                  meta_description: '',
                  is_published: false,
                };
                updateWebsiteField('pages', [...existingPages, newPage]);
              },
            },
          ],
          'plain-text'
        )
      : (() => {
          // Android Alert.prompt fallback — open page editor directly with a blank page
          const newPage = {
            title: 'New Page',
            slug: `page-${existingPages.length + 1}`,
            order: existingPages.length,
            content: '',
            meta_title: '',
            meta_description: '',
            is_published: false,
          };
          updateWebsiteField('pages', [...existingPages, newPage]);
          openPageEditor(newPage, existingPages.length);
        })();
  };

  /**
   * Open the page editor modal for a specific page.
   */
  const openPageEditor = (page, index) => {
    setEditingPage({ ...page });
    setEditingPageIndex(index);
    setShowPageEditor(true);
  };

  /**
   * Save edits to a single page back into the website's pages array.
   */
  const savePageEdits = () => {
    if (!editingWebsite || editingPageIndex < 0 || !editingPage) {
      setShowPageEditor(false);
      return;
    }
    const newPages = [...(editingWebsite.pages || [])];
    newPages[editingPageIndex] = { ...editingPage };
    updateWebsiteField('pages', newPages);
    setShowPageEditor(false);
    setEditingPage(null);
    setEditingPageIndex(-1);
  };

  /**
   * Update a field on the currently-editing page.
   */
  const updatePageField = (field, value) => {
    if (!editingPage) return;
    setEditingPage({ ...editingPage, [field]: value });
  };

  /**
   * Delete a page from the website.
   */
  const deletePage = (index) => {
    if (!editingWebsite) return;
    Alert.alert(
      'Delete Page?',
      'This page will be removed from your website.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            const newPages = (editingWebsite.pages || []).filter((_, i) => i !== index);
            // Renumber order
            newPages.forEach((p, i) => { p.order = i; });
            updateWebsiteField('pages', newPages);
          },
        },
      ]
    );
  };

  /**
   * Move a page up or down in the order.
   */
  const movePage = (index, direction) => {
    if (!editingWebsite) return;
    const pages = [...(editingWebsite.pages || [])];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= pages.length) return;
    [pages[index], pages[newIndex]] = [pages[newIndex], pages[index]];
    pages.forEach((p, i) => { p.order = i; });
    updateWebsiteField('pages', pages);
  };

  /**
   * Duplicate a page (useful for creating similar pages).
   */
  const duplicatePage = (index) => {
    if (!editingWebsite) return;
    const pages = [...(editingWebsite.pages || [])];
    const src = pages[index];
    if (!src) return;
    const copy = {
      ...src,
      title: `${src.title} (copy)`,
      slug: `${src.slug}-copy-${Date.now().toString(36).slice(-4)}`,
      order: pages.length,
    };
    updateWebsiteField('pages', [...pages, copy]);
  };

  /**
   * AI-generate content for a specific page.
   */
  const generatePageContent = async () => {
    if (!editingPage || !editingWebsite) return;
    setAiGenerating(true);
    try {
      const prompt = `Write website content for the "${editingPage.title}" page of a ${editingWebsite.template_name?.toLowerCase() || 'business'} website called "${editingWebsite.name}". Make it professional, engaging, and appropriate for the section.`;
      const r = await api.post(EP.aiGenerate, {
        prompt,
        tone: 'professional',
        length: 'medium',
        content_type: 'website_page',
        company_id: parseInt(companyId, 10) || 0,
      }).catch(() => null);

      const text = r?.data?.content || r?.data?.text || r?.data?.result || '';
      if (text) {
        updatePageField('content', text);
      } else {
        // Fallback template based on common page types
        const fallback = generatePageFallback(editingPage.title, editingWebsite);
        updatePageField('content', fallback);
      }
    } catch (e) {
      const fallback = generatePageFallback(editingPage.title, editingWebsite);
      updatePageField('content', fallback);
    } finally {
      setAiGenerating(false);
    }
  };

  const generatePageFallback = (pageTitle, website) => {
    const name = website.name || 'our company';
    const t = (pageTitle || '').toLowerCase();
    if (t.includes('about')) {
      return `# About ${name}\n\nWe are passionate about what we do. Founded with a vision to make a difference, ${name} has grown into a trusted name in our industry.\n\n## Our Story\n\nFrom humble beginnings to where we are today, our journey has been driven by a commitment to excellence.\n\n## Our Mission\n\nWe exist to deliver exceptional value to every client through quality, innovation, and dedicated service.\n\n## Our Values\n\n- **Integrity** — We do what we say\n- **Excellence** — We never settle for less\n- **Innovation** — We embrace new ideas`;
    }
    if (t.includes('service')) {
      return `# Our Services\n\nWe offer a comprehensive range of services tailored to meet your needs.\n\n## Core Offerings\n\n- **Consultation** — Expert advice tailored to your situation\n- **Implementation** — End-to-end execution by experienced professionals\n- **Support** — Ongoing assistance to keep you successful\n\n## Why Choose Us\n\nOur team combines deep expertise with a personal touch. Every engagement is a partnership built on trust and results.\n\n[Contact us](#contact) to discuss your project.`;
    }
    if (t.includes('contact')) {
      return `# Get in Touch\n\nWe'd love to hear from you. Reach out using any of the methods below.\n\n## Contact Information\n\n**Email:** hello@${(website.domain || 'example.com').replace(/^www\./, '')}\n**Phone:** +44 (0) 20 0000 0000\n**Address:** Your street address here\n\n## Business Hours\n\nMonday – Friday: 9:00 AM – 6:00 PM\nSaturday: 10:00 AM – 4:00 PM\nSunday: Closed\n\n## Send a Message\n\nFill out our contact form and we'll respond within one business day.`;
    }
    if (t.includes('home') || t.includes('hero')) {
      return `# Welcome to ${name}\n\nDiscover what makes us different. We bring expertise, passion, and dedication to every project.\n\n## What We Do\n\nWe help businesses like yours achieve their goals through proven strategies and personalized service.\n\n## Featured Highlights\n\n- Decades of combined experience\n- Trusted by leading clients\n- Award-winning approach\n\n[Get Started](#contact) — Let's build something great together.`;
    }
    if (t.includes('team')) {
      return `# Meet the Team\n\nOur strength is our people. Get to know the talent behind ${name}.\n\n## Leadership\n\nOur leadership team brings decades of industry experience and a shared commitment to our mission.\n\n## Specialists\n\nFrom strategists to creators, every team member is an expert in their field.\n\n## Join Us\n\nWe're always looking for talented people. [See open positions](#careers).`;
    }
    if (t.includes('testimonial') || t.includes('review')) {
      return `# What Our Clients Say\n\nDon't just take our word for it. Here's what people are saying about working with ${name}.\n\n> "Working with ${name} transformed how we approach our business. Their expertise and attention to detail are unmatched."\n> *— Sarah J., Operations Director*\n\n> "An absolute pleasure to work with from start to finish. They delivered on every promise."\n> *— Michael R., CEO*\n\n> "${name} doesn't just provide a service — they become part of your team."\n> *— Linda K., Marketing Lead*`;
    }
    // Generic fallback
    return `# ${pageTitle}\n\nContent for the ${pageTitle} section of ${name}.\n\nReplace this with your custom content. You can use Markdown formatting:\n\n- **Bold text**\n- *Italic text*\n- [Links](https://example.com)\n- Lists like this one\n\n## Subsections\n\nBreak your content into clear sections with headings.\n\n## Calls to Action\n\nGuide your visitors toward what you want them to do next.`;
  };

  // ══════════════════════════════════════════════════════════════════════════
  // AI content generation
  // ══════════════════════════════════════════════════════════════════════════
  const runAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      Alert.alert('Prompt required', 'Tell me what you want to generate.');
      return;
    }
    setAiGenerating(true);
    setAiResult('');
    try {
      const r = await api.post(EP.aiGenerate, {
        prompt: aiPrompt,
        tone: aiTone,
        length: aiLength,
        content_type: activeTab,
        company_id: parseInt(companyId, 10) || 0,
      }).catch(() => null);

      const text = r?.data?.content || r?.data?.text || r?.data?.result || '';
      if (text) {
        setAiResult(text);
      } else {
        // Fallback: simple template-based generation
        setAiResult(generateLocalTemplate(aiPrompt, aiTone, aiLength));
      }
    } catch (e) {
      setAiResult(generateLocalTemplate(aiPrompt, aiTone, aiLength));
    } finally {
      setAiGenerating(false);
    }
  };

  const generateLocalTemplate = (prompt, tone, length) => {
    const opener = tone === 'casual'  ? 'Hey there,'
                 : tone === 'formal'  ? 'Dear Sir/Madam,'
                 : tone === 'friendly'? 'Hi friend,'
                 : 'Hello,';
    const wordCount = length === 'short' ? 50 : length === 'long' ? 250 : 120;
    const body = [
      `Following up on the topic: ${prompt}.`,
      `Here's a thoughtful take based on your request.`,
      `${tone === 'professional' ? 'We have considered' : 'I have thought about'} this carefully, weighing the options that best align with your goals.`,
      `Key points to consider include practicality, impact, and timing.`,
      `Looking forward to your thoughts.`,
    ].join('\n\n');
    const closing = tone === 'casual' ? '\n\nCheers!' : '\n\nBest regards,';
    return `${opener}\n\n${body.slice(0, wordCount * 8)}${closing}`;
  };

  const useAIResult = () => {
    const field = activeTab === 'blog' ? 'content'
                : activeTab === 'campaigns' || activeTab === 'templates' ? 'body'
                : 'body';
    if (editingItem || formData[field] !== undefined) {
      setFormData({ ...formData, [field]: aiResult });
    } else {
      openCreate({ [field]: aiResult });
    }
    setShowAIModal(false);
    setAiResult('');
    setAiPrompt('');
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Bulk operations
  // ══════════════════════════════════════════════════════════════════════════
  const toggleSelection = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const selectAll = () => setSelectedIds(new Set(filtered.map(i => i.id)));
  const clearSelection = () => setSelectedIds(new Set());

  const bulkDelete = () => {
    Alert.alert(
      `Delete ${selectedIds.size} items?`,
      'This cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete All',
          style: 'destructive',
          onPress: async () => {
            const idsToDelete = Array.from(selectedIds);
            setCurrentCollection(currentCollection.filter(i => !selectedIds.has(i.id)));
            clearSelection();
            setShowBulkModal(false);
            try {
              await Promise.all(idsToDelete.map(id =>
                api.delete(`${endpointForTab()}/${id}`).catch(() => null)
              ));
            } catch {}
          },
        },
      ]
    );
  };

  const bulkSetStatus = async (newStatus) => {
    const idsToUpdate = Array.from(selectedIds);
    setCurrentCollection(currentCollection.map(i =>
      selectedIds.has(i.id) ? { ...i, status: newStatus } : i
    ));
    clearSelection();
    setShowBulkModal(false);
    try {
      await Promise.all(idsToUpdate.map(id =>
        api.patch(`${endpointForTab()}/${id}`, { status: newStatus }).catch(() => null)
      ));
    } catch {}
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Sharing
  // ══════════════════════════════════════════════════════════════════════════
  const shareItem = async (item) => {
    try {
      const text = item.body || item.content || item.description || item.name || '';
      await Share.share({
        message: `${item.title || item.name}\n\n${text}`.slice(0, 2000),
        title: item.title || item.name,
        url: item.file_url || item.url,
      });
    } catch {}
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Render — Header
  // ══════════════════════════════════════════════════════════════════════════
  const renderHeader = () => (
    <LinearGradient colors={[COLORS.accent, COLORS.accentLt]} style={st.header}>
      <View style={st.headerRow}>
        <TouchableOpacity onPress={() => navigation?.goBack()} style={st.hBtn}>
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFF" />
        </TouchableOpacity>
        <View style={{ flex: 1, marginLeft: 12 }}>
          <Text style={st.hTitle}>{companyProfile?.name || 'Content Management'}</Text>
          <Text style={st.hSub}>
            {companyProfile?.industry ? `${companyProfile.industry} · ` : ''}
            Content Management
          </Text>
        </View>
        <TouchableOpacity style={st.hBtn} onPress={() => setShowAnalytics(true)}>
          <MaterialCommunityIcons name="chart-bar" size={22} color="#FFF" />
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );

  // ── Stats bar ──────────────────────────────────────────────────────────────
  const renderStats = () => (
    <ScrollView
      horizontal showsHorizontalScrollIndicator={false}
      style={st.statsBar}
      contentContainerStyle={{ paddingHorizontal: 16 }}
    >
      {TABS.map(t => (
        <TouchableOpacity
          key={t.id}
          style={st.statCard}
          onPress={() => setActiveTab(t.id)}
        >
          <View style={[st.statIconWrap, { backgroundColor: `${t.color}15` }]}>
            <MaterialCommunityIcons name={t.icon} size={18} color={t.color} />
          </View>
          <Text style={[st.statVal, { color: t.color }]}>{stats[t.id === 'blog' ? 'blogPosts' : t.id] || 0}</Text>
          <Text style={st.statLbl}>{t.label}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  // ── Tabs ───────────────────────────────────────────────────────────────────
  const renderTabs = () => (
    <View style={st.tabsRow}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 12 }}>
        {TABS.map(tab => (
          <TouchableOpacity
            key={tab.id}
            style={[st.tab, activeTab === tab.id && { backgroundColor: `${tab.color}15` }]}
            onPress={() => setActiveTab(tab.id)}
          >
            <MaterialCommunityIcons
              name={tab.icon}
              size={18}
              color={activeTab === tab.id ? tab.color : COLORS.sub}
            />
            <Text style={[st.tabTxt, activeTab === tab.id && { color: tab.color, fontWeight: '700' }]}>
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  // ── Search + filters ───────────────────────────────────────────────────────
  const renderToolbar = () => (
    <View style={st.toolbar}>
      <View style={st.searchRow}>
        <MaterialCommunityIcons name="magnify" size={20} color={COLORS.light} />
        <TextInput
          style={st.searchInput}
          placeholder={`Search ${String(activeTabMeta.label ?? '').toLowerCase()}...`}
          placeholderTextColor={COLORS.light}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        {searchQuery ? (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons name="close-circle" size={18} color={COLORS.light} />
          </TouchableOpacity>
        ) : null}
      </View>
      <TouchableOpacity style={st.iconBtn} onPress={() => setShowFilterModal(true)}>
        <MaterialCommunityIcons name="filter-variant" size={22} color={statusFilter !== 'all' ? activeTabMeta.color : COLORS.sub} />
      </TouchableOpacity>
      {selectedIds.size > 0 ? (
        <TouchableOpacity style={[st.iconBtn, { backgroundColor: `${activeTabMeta.color}15` }]} onPress={() => setShowBulkModal(true)}>
          <Text style={[st.iconBtnBadge, { color: activeTabMeta.color }]}>{selectedIds.size}</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );

  // ── Item card dispatcher ──────────────────────────────────────────────────
  const renderItemCard = (item, idx) => {
    if (activeTab === 'websites') return renderWebsiteCard(item, idx);
    return renderGenericCard(item, idx);
  };

  // ── Website card (rich layout — different from generic cards) ─────────────
  const renderWebsiteCard = (item, idx) => {
    const isActive = activeWebsiteId === item.id;
    const tmpl = WEBSITE_TEMPLATES.find(t => t.id === item.template_id) || { name: item.template_name || 'Custom', color: '#06B6D4', icon: 'web' };
    const pageCount = Array.isArray(item.pages) ? item.pages.length : (Array.isArray(item.sections) ? item.sections.length : 0);

    return (
      <View key={item.id || idx} style={[st.websiteCard, isActive && { borderColor: tmpl.color, borderWidth: 2 }]}>
        <LinearGradient colors={[tmpl.color, `${tmpl.color}CC`]} style={st.websiteHeader}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <MaterialCommunityIcons name={tmpl.icon} size={28} color="#FFF" />
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={st.websiteName}>{item.name || 'Untitled site'}</Text>
              {item.domain ? (
                <Text style={st.websiteDomain}>{item.domain}</Text>
              ) : item.source_url ? (
                <Text style={st.websiteDomain} numberOfLines={1}>From: {item.source_url}</Text>
              ) : (
                <Text style={st.websiteDomain}>No domain set</Text>
              )}
            </View>
            {isActive && (
              <View style={st.activeChip}>
                <Text style={st.activeChipTxt}>ACTIVE</Text>
              </View>
            )}
          </View>
        </LinearGradient>

        <View style={st.websiteMeta}>
          <View style={st.websiteMetaItem}>
            <MaterialCommunityIcons name="palette-outline" size={14} color={COLORS.sub} />
            <Text style={st.websiteMetaTxt}>{tmpl.name}</Text>
          </View>
          <View style={st.websiteMetaItem}>
            <MaterialCommunityIcons name="file-multiple-outline" size={14} color={COLORS.sub} />
            <Text style={st.websiteMetaTxt}>{pageCount} pages</Text>
          </View>
          {item.status && (
            <View style={st.websiteMetaItem}>
              <View style={[st.statusDot, { backgroundColor: STATUS_COLORS[item.status] || COLORS.sub }]} />
              <Text style={[st.websiteMetaTxt, { textTransform: 'capitalize' }]}>{item.status}</Text>
            </View>
          )}
          {item.local_only && (
            <View style={st.websiteMetaItem}>
              <MaterialCommunityIcons name="cloud-off-outline" size={14} color={COLORS.warning} />
              <Text style={[st.websiteMetaTxt, { color: COLORS.warning }]}>Local only</Text>
            </View>
          )}
        </View>

        <View style={st.websiteActions}>
          <TouchableOpacity style={[st.websiteBtn, { backgroundColor: tmpl.color }]} onPress={() => openWebsiteBuilder(item)}>
            <MaterialCommunityIcons name="pencil-outline" size={14} color="#FFF" />
            <Text style={st.websiteBtnTxt}>Open Builder</Text>
          </TouchableOpacity>
          {!isActive && (
            <TouchableOpacity style={[st.websiteBtn, { backgroundColor: COLORS.bgAlt }]} onPress={() => setActiveWebsite(item)}>
              <MaterialCommunityIcons name="star-outline" size={14} color={COLORS.text} />
              <Text style={[st.websiteBtnTxt, { color: COLORS.text }]}>Set Active</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity style={[st.websiteBtn, { backgroundColor: COLORS.bgAlt }]} onPress={() => { setSelectedItem(item); setShowDetailModal(true); }}>
            <MaterialCommunityIcons name="dots-horizontal" size={16} color={COLORS.text} />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  // ── Item card (generic, for non-website tabs) ─────────────────────────────
  const renderGenericCard = (item, idx) => {
    const isSelected = selectedIds.has(item.id);
    const title = item.title || item.name || `Item #${item.id}`;
    const subtitle = item.subject || item.excerpt || item.description || item.body || item.type || '';
    const statusColor = STATUS_COLORS[item.status] || COLORS.sub;

    return (
      <TouchableOpacity
        key={item.id || idx}
        style={[st.card, isSelected && st.cardSelected]}
        onPress={() => {
          if (selectedIds.size > 0) {
            toggleSelection(item.id);
          } else {
            setSelectedItem(item);
            setShowDetailModal(true);
          }
        }}
        onLongPress={() => toggleSelection(item.id)}
        delayLongPress={250}
      >
        <View style={st.cardRow}>
          {selectedIds.size > 0 && (
            <View style={[st.checkbox, isSelected && { backgroundColor: activeTabMeta.color, borderColor: activeTabMeta.color }]}>
              {isSelected && <MaterialCommunityIcons name="check" size={14} color="#FFF" />}
            </View>
          )}
          <View style={[st.avatar, { backgroundColor: activeTabMeta.color }]}>
            <MaterialCommunityIcons name={activeTabMeta.icon} size={20} color="#FFF" />
          </View>
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={st.cardName} numberOfLines={1}>{title}</Text>
            {subtitle ? <Text style={st.cardMeta} numberOfLines={2}>{String(subtitle).slice(0, 100)}</Text> : null}
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
              {item.status ? (
                <View style={[st.badge, { backgroundColor: `${statusColor}20` }]}>
                  <Text style={[st.badgeTxt, { color: statusColor }]}>{item.status}</Text>
                </View>
              ) : null}
              {item.category ? (
                <View style={[st.badge, { backgroundColor: COLORS.bg, marginLeft: 6 }]}>
                  <Text style={[st.badgeTxt, { color: COLORS.sub }]}>{item.category}</Text>
                </View>
              ) : null}
              {item.channel ? (
                <View style={[st.badge, { backgroundColor: `${COLORS.primary}15`, marginLeft: 6 }]}>
                  <MaterialCommunityIcons
                    name={item.channel === 'email' ? 'email' : item.channel === 'sms' ? 'message' : 'bullhorn'}
                    size={11}
                    color={COLORS.primary}
                  />
                  <Text style={[st.badgeTxt, { color: COLORS.primary, marginLeft: 4 }]}>{item.channel}</Text>
                </View>
              ) : null}
              {item.created_at ? <Text style={st.cardDate}>{formatDate(item.created_at)}</Text> : null}
            </View>
          </View>
          {(item.value || item.total || item.revenue) > 0 ? (
            <Text style={st.cardVal}>{formatCurrency(item.value || item.total || item.revenue)}</Text>
          ) : (
            <MaterialCommunityIcons name="chevron-right" size={20} color={COLORS.light} />
          )}
        </View>
      </TouchableOpacity>
    );
  };

  // ── Empty state ────────────────────────────────────────────────────────────
  const renderEmpty = () => {
    if (activeTab === 'websites') {
      return (
        <View style={st.empty}>
          <MaterialCommunityIcons name="web" size={64} color={COLORS.border} />
          <Text style={st.emptyTitle}>No websites yet</Text>
          <Text style={st.emptySub}>Build a professional site from a template, or copy an existing one</Text>
          <View style={{ flexDirection: 'row', marginTop: 16, gap: 10 }}>
            <TouchableOpacity style={[st.emptyBtn, { backgroundColor: '#06B6D4' }]} onPress={() => setShowWebsiteCreate(true)}>
              <MaterialCommunityIcons name="plus" size={18} color="#FFF" />
              <Text style={st.emptyBtnTxt}>Create New</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[st.emptyBtn, { backgroundColor: COLORS.purple }]} onPress={() => setShowWebsiteClone(true)}>
              <MaterialCommunityIcons name="content-duplicate" size={18} color="#FFF" />
              <Text style={st.emptyBtnTxt}>Clone Existing</Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }
    return (
      <View style={st.empty}>
        <MaterialCommunityIcons name={activeTabMeta.icon} size={64} color={COLORS.border} />
        <Text style={st.emptyTitle}>No {String(activeTabMeta.label ?? '').toLowerCase()} yet</Text>
        <Text style={st.emptySub}>Create your first one or generate with AI</Text>
        <View style={{ flexDirection: 'row', marginTop: 16, gap: 10 }}>
          <TouchableOpacity style={[st.emptyBtn, { backgroundColor: activeTabMeta.color }]} onPress={() => openCreate()}>
            <MaterialCommunityIcons name="plus" size={18} color="#FFF" />
            <Text style={st.emptyBtnTxt}>Create</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[st.emptyBtn, { backgroundColor: COLORS.purple }]} onPress={() => setShowAIModal(true)}>
            <MaterialCommunityIcons name="sparkles" size={18} color="#FFF" />
            <Text style={st.emptyBtnTxt}>AI Generate</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  // ── List ───────────────────────────────────────────────────────────────────
  const renderList = () => {
    if (filtered.length === 0) return renderEmpty();
    return (
      <View style={{ paddingVertical: 8 }}>
        {filtered.slice(0, 100).map((item, i) => renderItemCard(item, i))}
        {filtered.length > 100 && (
          <Text style={st.moreNote}>Showing first 100 of {filtered.length}. Refine search to see more.</Text>
        )}
      </View>
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Modal: Create / Edit
  // ══════════════════════════════════════════════════════════════════════════
  const renderCreateModal = () => {
    const fields = createFieldsForTab();
    return (
      <Modal visible={showCreateModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowCreateModal(false)}>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <SafeAreaView style={st.modal}>
            <View style={st.modalH}>
              <TouchableOpacity onPress={() => setShowCreateModal(false)}>
                <MaterialCommunityIcons name="close" size={26} color={COLORS.text} />
              </TouchableOpacity>
              <Text style={st.modalTitle}>{editingItem ? 'Edit' : 'New'} {activeTabMeta.label.slice(0, -1)}</Text>
              <TouchableOpacity onPress={saveItem} disabled={saving}>
                {saving ? <ActivityIndicator color={activeTabMeta.color} /> : <Text style={[st.saveBtn, { color: activeTabMeta.color }]}>Save</Text>}
              </TouchableOpacity>
            </View>

            <ScrollView style={{ flex: 1, padding: 16 }} keyboardShouldPersistTaps="handled">
              {fields.map(field => renderField(field))}

              <TouchableOpacity
                style={[st.aiAssistBtn, { backgroundColor: `${COLORS.purple}15` }]}
                onPress={() => setShowAIModal(true)}
              >
                <MaterialCommunityIcons name="sparkles" size={18} color={COLORS.purple} />
                <Text style={[st.aiAssistTxt, { color: COLORS.purple }]}>AI Assist</Text>
              </TouchableOpacity>
            </ScrollView>
          </SafeAreaView>
        </KeyboardAvoidingView>
      </Modal>
    );
  };

  const createFieldsForTab = () => {
    switch (activeTab) {
      case 'documents':
        return [
          { key: 'name', label: 'Document Name', required: true },
          { key: 'type', label: 'Type', placeholder: 'pdf, docx, image...' },
          { key: 'description', label: 'Description', multiline: true, rows: 4 },
          { key: 'file_url', label: 'File URL' },
          { key: 'status', label: 'Status', type: 'select', options: ['active', 'archived'] },
        ];
      case 'templates':
        return [
          { key: 'name', label: 'Template Name', required: true },
          { key: 'type', label: 'Type', type: 'select', options: ['email', 'letter', 'sms'] },
          { key: 'subject', label: 'Subject' },
          { key: 'body', label: 'Body', required: true, multiline: true, rows: 10 },
          { key: 'variables', label: 'Variables (comma-separated, e.g. {{firstName}}, {{company}})' },
        ];
      case 'letters':
        return [
          { key: 'title', label: 'Title', required: true },
          { key: 'recipient', label: 'Recipient' },
          { key: 'category', label: 'Category', type: 'select', options: ['general', 'offer', 'rejection', 'welcome', 'reference'] },
          { key: 'body', label: 'Body', required: true, multiline: true, rows: 10 },
          { key: 'status', label: 'Status', type: 'select', options: ['draft', 'sent', 'archived'] },
        ];
      case 'campaigns':
        return [
          { key: 'name', label: 'Campaign Name', required: true },
          { key: 'channel', label: 'Channel', type: 'select', options: ['email', 'sms', 'push', 'social'] },
          { key: 'subject', label: 'Subject' },
          { key: 'body', label: 'Message', multiline: true, rows: 8 },
          { key: 'scheduled_at', label: 'Schedule (YYYY-MM-DD HH:MM)', placeholder: '2025-06-01 09:00' },
          { key: 'status', label: 'Status', type: 'select', options: ['draft', 'scheduled', 'active', 'paused', 'archived'] },
        ];
      case 'blog':
        return [
          { key: 'title', label: 'Title', required: true },
          { key: 'slug', label: 'URL Slug', placeholder: 'my-post-title' },
          { key: 'category', label: 'Category' },
          { key: 'tags', label: 'Tags (comma-separated)' },
          { key: 'excerpt', label: 'Excerpt', multiline: true, rows: 3 },
          { key: 'content', label: 'Content', required: true, multiline: true, rows: 14 },
          { key: 'author', label: 'Author' },
          { key: 'status', label: 'Status', type: 'select', options: ['draft', 'published', 'scheduled', 'archived'] },
        ];
      case 'media':
        return [
          { key: 'name', label: 'File Name', required: true },
          { key: 'type', label: 'Type', type: 'select', options: ['image', 'video', 'audio', 'document'] },
          { key: 'url', label: 'URL', required: true },
          { key: 'alt_text', label: 'Alt Text' },
          { key: 'size_bytes', label: 'Size (bytes)' },
        ];
      default:
        return [];
    }
  };

  const renderField = (field) => {
    if (field.type === 'select') {
      return (
        <View key={field.key} style={st.formField}>
          <Text style={st.formLbl}>{field.label}{field.required && <Text style={{ color: COLORS.danger }}> *</Text>}</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingVertical: 4 }}>
            {field.options.map(opt => (
              <TouchableOpacity
                key={opt}
                style={[st.selectChip, formData[field.key] === opt && { backgroundColor: activeTabMeta.color, borderColor: activeTabMeta.color }]}
                onPress={() => setFormData({ ...formData, [field.key]: opt })}
              >
                <Text style={[st.selectChipTxt, formData[field.key] === opt && { color: '#FFF', fontWeight: '700' }]}>{opt}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      );
    }
    return (
      <View key={field.key} style={st.formField}>
        <Text style={st.formLbl}>{field.label}{field.required && <Text style={{ color: COLORS.danger }}> *</Text>}</Text>
        <TextInput
          style={[st.formInput, field.multiline && { height: 28 * (field.rows || 4), textAlignVertical: 'top' }]}
          value={String(formData[field.key] || '')}
          onChangeText={(v) => setFormData({ ...formData, [field.key]: v })}
          placeholder={field.placeholder}
          placeholderTextColor={COLORS.light}
          multiline={field.multiline}
        />
      </View>
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Modal: Detail
  // ══════════════════════════════════════════════════════════════════════════
  const renderDetailModal = () => (
    <Modal visible={showDetailModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowDetailModal(false)}>
      <SafeAreaView style={st.modal}>
        <View style={st.modalH}>
          <TouchableOpacity onPress={() => setShowDetailModal(false)}>
            <MaterialCommunityIcons name="close" size={26} color={COLORS.text} />
          </TouchableOpacity>
          <Text style={st.modalTitle} numberOfLines={1}>{selectedItem?.title || selectedItem?.name || 'Details'}</Text>
          <TouchableOpacity onPress={() => selectedItem && shareItem(selectedItem)}>
            <MaterialCommunityIcons name="share-variant" size={22} color={COLORS.primary} />
          </TouchableOpacity>
        </View>

        {selectedItem && (
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 32 }}>
            <View style={st.detailActions}>
              <TouchableOpacity style={[st.detailActBtn, { backgroundColor: COLORS.primary }]} onPress={() => openEdit(selectedItem)}>
                <MaterialCommunityIcons name="pencil" size={16} color="#FFF" />
                <Text style={st.detailActTxt}>Edit</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[st.detailActBtn, { backgroundColor: COLORS.purple }]} onPress={() => duplicateItem(selectedItem)}>
                <MaterialCommunityIcons name="content-copy" size={16} color="#FFF" />
                <Text style={st.detailActTxt}>Duplicate</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[st.detailActBtn, { backgroundColor: COLORS.success }]} onPress={() => toggleStatus(selectedItem)}>
                <MaterialCommunityIcons name="swap-horizontal" size={16} color="#FFF" />
                <Text style={st.detailActTxt}>Toggle Status</Text>
              </TouchableOpacity>
              {(activeTab === 'blog' || activeTab === 'media' || activeTab === 'documents') && (
                <TouchableOpacity style={[st.detailActBtn, { backgroundColor: '#06B6D4' }]} onPress={() => openPublishPicker(selectedItem)}>
                  <MaterialCommunityIcons name="web" size={16} color="#FFF" />
                  <Text style={st.detailActTxt}>Publish to Site</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={[st.detailActBtn, { backgroundColor: COLORS.danger }]} onPress={() => deleteItem(selectedItem)}>
                <MaterialCommunityIcons name="delete" size={16} color="#FFF" />
                <Text style={st.detailActTxt}>Delete</Text>
              </TouchableOpacity>
            </View>

            {Object.entries(selectedItem)
              .filter(([k, v]) => typeof v !== 'object' && !['id', 'company_id'].includes(k))
              .map(([k, v]) => (
                <View key={k} style={st.detailRow}>
                  <Text style={st.detailKey}>{k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Text>
                  <Text style={st.detailVal} selectable>{String(v || 'N/A')}</Text>
                </View>
              ))}
          </ScrollView>
        )}
      </SafeAreaView>
    </Modal>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // Modal: AI Generate
  // ══════════════════════════════════════════════════════════════════════════
  const renderAIModal = () => (
    <Modal visible={showAIModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAIModal(false)}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <SafeAreaView style={st.modal}>
          <View style={st.modalH}>
            <TouchableOpacity onPress={() => setShowAIModal(false)}>
              <MaterialCommunityIcons name="close" size={26} color={COLORS.text} />
            </TouchableOpacity>
            <Text style={st.modalTitle}>AI Generate</Text>
            <View style={{ width: 26 }} />
          </View>

          <ScrollView style={{ flex: 1, padding: 16 }}>
            <Text style={st.formLbl}>What do you want to create?</Text>
            <TextInput
              style={[st.formInput, { height: 100, textAlignVertical: 'top' }]}
              value={aiPrompt}
              onChangeText={setAiPrompt}
              multiline
              placeholder={`E.g. "A welcome email for new customers in the construction industry"`}
              placeholderTextColor={COLORS.light}
            />

            <Text style={st.formLbl}>Tone</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {['professional', 'friendly', 'casual', 'formal', 'enthusiastic'].map(t => (
                <TouchableOpacity
                  key={t}
                  style={[st.selectChip, aiTone === t && { backgroundColor: COLORS.purple, borderColor: COLORS.purple }]}
                  onPress={() => setAiTone(t)}
                >
                  <Text style={[st.selectChipTxt, aiTone === t && { color: '#FFF', fontWeight: '700' }]}>{t}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <Text style={st.formLbl}>Length</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {['short', 'medium', 'long'].map(t => (
                <TouchableOpacity
                  key={t}
                  style={[st.selectChip, aiLength === t && { backgroundColor: COLORS.purple, borderColor: COLORS.purple }]}
                  onPress={() => setAiLength(t)}
                >
                  <Text style={[st.selectChipTxt, aiLength === t && { color: '#FFF', fontWeight: '700' }]}>{t}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <TouchableOpacity
              style={[st.aiGenBtn, (!aiPrompt.trim() || aiGenerating) && { backgroundColor: COLORS.light }]}
              onPress={runAIGenerate}
              disabled={!aiPrompt.trim() || aiGenerating}
            >
              {aiGenerating ? <ActivityIndicator color="#FFF" /> : (
                <>
                  <MaterialCommunityIcons name="sparkles" size={18} color="#FFF" />
                  <Text style={st.aiGenBtnTxt}>{aiResult ? 'Regenerate' : 'Generate'}</Text>
                </>
              )}
            </TouchableOpacity>

            {aiResult ? (
              <>
                <Text style={[st.formLbl, { marginTop: 16 }]}>Generated</Text>
                <View style={st.aiResultBox}>
                  <Text style={st.aiResultText} selectable>{aiResult}</Text>
                </View>
                <TouchableOpacity style={[st.aiUseBtn, { backgroundColor: activeTabMeta.color }]} onPress={useAIResult}>
                  <MaterialCommunityIcons name="check" size={18} color="#FFF" />
                  <Text style={st.aiUseBtnTxt}>Use this content</Text>
                </TouchableOpacity>
              </>
            ) : null}
          </ScrollView>
        </SafeAreaView>
      </KeyboardAvoidingView>
    </Modal>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // Modal: Filter
  // ══════════════════════════════════════════════════════════════════════════
  const availableStatuses = useMemo(() => {
    const set = new Set(['all']);
    currentCollection.forEach(i => { if (i.status) set.add(i.status); });
    return Array.from(set);
  }, [currentCollection]);

  const renderFilterModal = () => (
    <Modal visible={showFilterModal} animationType="fade" transparent onRequestClose={() => setShowFilterModal(false)}>
      <Pressable style={st.filterBackdrop} onPress={() => setShowFilterModal(false)}>
        <Pressable style={st.filterSheet}>
          <Text style={st.modalTitle}>Filter by Status</Text>
          <View style={{ marginTop: 12 }}>
            {availableStatuses.map(s => (
              <TouchableOpacity
                key={s}
                style={[st.filterOption, statusFilter === s && { backgroundColor: `${activeTabMeta.color}15` }]}
                onPress={() => { setStatusFilter(s); setShowFilterModal(false); }}
              >
                <MaterialCommunityIcons
                  name={statusFilter === s ? 'radiobox-marked' : 'radiobox-blank'}
                  size={20}
                  color={statusFilter === s ? activeTabMeta.color : COLORS.sub}
                />
                <Text style={[st.filterOptionTxt, statusFilter === s && { color: activeTabMeta.color, fontWeight: '700' }]}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // Modal: Bulk operations
  // ══════════════════════════════════════════════════════════════════════════
  const renderBulkModal = () => (
    <Modal visible={showBulkModal} animationType="fade" transparent onRequestClose={() => setShowBulkModal(false)}>
      <Pressable style={st.filterBackdrop} onPress={() => setShowBulkModal(false)}>
        <Pressable style={st.filterSheet}>
          <Text style={st.modalTitle}>Bulk Actions ({selectedIds.size} selected)</Text>
          <View style={{ marginTop: 12 }}>
            <TouchableOpacity style={st.bulkAction} onPress={selectAll}>
              <MaterialCommunityIcons name="select-all" size={20} color={COLORS.primary} />
              <Text style={st.bulkActionTxt}>Select All</Text>
            </TouchableOpacity>
            <TouchableOpacity style={st.bulkAction} onPress={() => bulkSetStatus('active')}>
              <MaterialCommunityIcons name="check-circle" size={20} color={COLORS.success} />
              <Text style={st.bulkActionTxt}>Mark Active</Text>
            </TouchableOpacity>
            <TouchableOpacity style={st.bulkAction} onPress={() => bulkSetStatus('archived')}>
              <MaterialCommunityIcons name="archive" size={20} color={COLORS.sub} />
              <Text style={st.bulkActionTxt}>Archive</Text>
            </TouchableOpacity>
            <TouchableOpacity style={st.bulkAction} onPress={bulkDelete}>
              <MaterialCommunityIcons name="delete" size={20} color={COLORS.danger} />
              <Text style={[st.bulkActionTxt, { color: COLORS.danger }]}>Delete All</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[st.bulkAction, { borderTopWidth: 1, borderTopColor: COLORS.border, marginTop: 4 }]} onPress={clearSelection}>
              <MaterialCommunityIcons name="close" size={20} color={COLORS.sub} />
              <Text style={st.bulkActionTxt}>Clear Selection</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // Modal: Analytics
  // ══════════════════════════════════════════════════════════════════════════
  const renderAnalyticsModal = () => (
    <Modal visible={showAnalytics} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAnalytics(false)}>
      <SafeAreaView style={st.modal}>
        <View style={st.modalH}>
          <TouchableOpacity onPress={() => setShowAnalytics(false)}>
            <MaterialCommunityIcons name="close" size={26} color={COLORS.text} />
          </TouchableOpacity>
          <Text style={st.modalTitle}>Content Analytics</Text>
          <View style={{ width: 26 }} />
        </View>

        <ScrollView style={{ flex: 1, padding: 16 }}>
          <Text style={st.anaSection}>Volume by Type</Text>
          <View style={st.grid}>
            {TABS.map(t => (
              <View key={t.id} style={st.gridCard}>
                <View style={[st.statIconWrap, { backgroundColor: `${t.color}15`, marginBottom: 6 }]}>
                  <MaterialCommunityIcons name={t.icon} size={20} color={t.color} />
                </View>
                <Text style={[st.gridVal, { color: t.color }]}>{stats[t.id === 'blog' ? 'blogPosts' : t.id] || 0}</Text>
                <Text style={st.gridLbl}>{t.label}</Text>
              </View>
            ))}
          </View>

          <Text style={[st.anaSection, { marginTop: 24 }]}>Status Breakdown — {activeTabMeta.label}</Text>
          {availableStatuses.filter(s => s !== 'all').map(s => {
            const count = currentCollection.filter(i => i.status === s).length;
            const pct = currentCollection.length ? Math.round((count / currentCollection.length) * 100) : 0;
            return (
              <View key={s} style={st.barRow}>
                <Text style={st.barLbl}>{s}</Text>
                <View style={st.barBg}>
                  <View style={[st.barFill, { width: `${pct}%`, backgroundColor: STATUS_COLORS[s] || activeTabMeta.color }]} />
                </View>
                <Text style={st.barCnt}>{count}</Text>
              </View>
            );
          })}

          <Text style={[st.anaSection, { marginTop: 24 }]}>Total Content Items</Text>
          <View style={st.totalCard}>
            <Text style={st.totalNum}>{Object.values(stats).reduce((a, b) => a + (b || 0), 0)}</Text>
            <Text style={st.totalLbl}>across all content types</Text>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // Modal: Create Website (template picker)
  // ══════════════════════════════════════════════════════════════════════════
  const renderWebsiteCreateModal = () => {
    const [step, setStep] = [0, () => {}]; // simple inline state — picker only
    const NameField = () => {
      const [name, setName]     = useState(formData.websiteName || '');
      const [domain, setDomain] = useState(formData.websiteDomain || '');
      const tmpl = formData.websiteTemplate;
      return (
        <View style={{ padding: 16 }}>
          <Text style={st.formLbl}>Site Name <Text style={{ color: COLORS.danger }}>*</Text></Text>
          <TextInput
            style={st.formInput}
            value={name}
            onChangeText={setName}
            placeholder="e.g. Acme Construction"
            placeholderTextColor={COLORS.light}
          />
          <Text style={st.formLbl}>Domain (Optional)</Text>
          <TextInput
            style={st.formInput}
            value={domain}
            onChangeText={setDomain}
            placeholder="acme-construction.com"
            autoCapitalize="none"
            autoCorrect={false}
            placeholderTextColor={COLORS.light}
          />
          <View style={{ marginTop: 14, padding: 12, backgroundColor: `${tmpl.color}10`, borderRadius: 10 }}>
            <Text style={{ fontSize: 12, color: COLORS.sub, marginBottom: 6, fontWeight: '600', textTransform: 'uppercase' }}>Will include:</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              {tmpl.sections.map(s => (
                <View key={s} style={{ paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, backgroundColor: COLORS.surface, borderWidth: 1, borderColor: COLORS.border }}>
                  <Text style={{ fontSize: 12, color: COLORS.text }}>{s}</Text>
                </View>
              ))}
            </View>
          </View>
          <TouchableOpacity
            style={[st.aiUseBtn, { backgroundColor: tmpl.color, marginTop: 20 }, (!name.trim() || saving) && { backgroundColor: COLORS.light }]}
            onPress={() => createWebsiteFromTemplate(tmpl, name, domain)}
            disabled={!name.trim() || saving}
          >
            {saving ? <ActivityIndicator color="#FFF" /> : (
              <>
                <MaterialCommunityIcons name="rocket-launch" size={18} color="#FFF" />
                <Text style={st.aiUseBtnTxt}>Create Website</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      );
    };

    return (
      <Modal visible={showWebsiteCreate} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowWebsiteCreate(false)}>
        <SafeAreaView style={st.modal}>
          <View style={st.modalH}>
            <TouchableOpacity onPress={() => {
              if (formData.websiteTemplate) {
                setFormData({ ...formData, websiteTemplate: null });
              } else {
                setShowWebsiteCreate(false);
              }
            }}>
              <MaterialCommunityIcons
                name={formData.websiteTemplate ? 'arrow-left' : 'close'}
                size={26}
                color={COLORS.text}
              />
            </TouchableOpacity>
            <Text style={st.modalTitle}>
              {formData.websiteTemplate ? 'Site Details' : 'Choose Template'}
            </Text>
            <View style={{ width: 26 }} />
          </View>

          <ScrollView style={{ flex: 1 }}>
            {!formData.websiteTemplate ? (
              // Template picker
              <View style={{ padding: 16 }}>
                <Text style={{ fontSize: 13, color: COLORS.sub, marginBottom: 16, lineHeight: 18 }}>
                  Pick a starting template. Each one comes with pre-built sections you can customize.
                </Text>
                {WEBSITE_TEMPLATES.map(t => (
                  <TouchableOpacity
                    key={t.id}
                    style={st.templateCard}
                    onPress={() => setFormData({ ...formData, websiteTemplate: t })}
                  >
                    <View style={[st.templateIconBox, { backgroundColor: `${t.color}15` }]}>
                      <MaterialCommunityIcons name={t.icon} size={28} color={t.color} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={st.templateName}>{t.name}</Text>
                      <Text style={st.templateDesc}>{t.description}</Text>
                      <Text style={st.templateSections}>{t.sections.length} sections</Text>
                    </View>
                    <MaterialCommunityIcons name="chevron-right" size={20} color={COLORS.light} />
                  </TouchableOpacity>
                ))}
              </View>
            ) : (
              <NameField />
            )}
          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Modal: Clone Website (from URL)
  // ══════════════════════════════════════════════════════════════════════════
  const renderWebsiteCloneModal = () => (
    <Modal visible={showWebsiteClone} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowWebsiteClone(false)}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <SafeAreaView style={st.modal}>
          <View style={st.modalH}>
            <TouchableOpacity onPress={() => setShowWebsiteClone(false)}>
              <MaterialCommunityIcons name="close" size={26} color={COLORS.text} />
            </TouchableOpacity>
            <Text style={st.modalTitle}>Clone Website</Text>
            <View style={{ width: 26 }} />
          </View>

          <ScrollView style={{ flex: 1, padding: 16 }}>
            <View style={{ padding: 14, backgroundColor: `${COLORS.purple}10`, borderRadius: 10, marginBottom: 16, flexDirection: 'row' }}>
              <MaterialCommunityIcons name="information-outline" size={20} color={COLORS.purple} style={{ marginRight: 10, marginTop: 1 }} />
              <Text style={{ flex: 1, fontSize: 12, color: COLORS.text, lineHeight: 17 }}>
                Paste any public website URL and we'll create a copy you can customize. Your version is fully editable — no original content is modified.
              </Text>
            </View>

            <Text style={st.formLbl}>Website URL <Text style={{ color: COLORS.danger }}>*</Text></Text>
            <TextInput
              style={st.formInput}
              value={cloneUrl}
              onChangeText={setCloneUrl}
              placeholder="https://example.com"
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="url"
              placeholderTextColor={COLORS.light}
            />
            <Text style={{ fontSize: 11, color: COLORS.sub, marginTop: 6 }}>
              Include http:// or https:// at the start
            </Text>

            <TouchableOpacity
              style={[st.aiGenBtn, { backgroundColor: COLORS.purple }, (!cloneUrl.trim() || cloning) && { backgroundColor: COLORS.light }]}
              onPress={cloneWebsiteFromUrl}
              disabled={!cloneUrl.trim() || cloning}
            >
              {cloning ? <ActivityIndicator color="#FFF" /> : (
                <>
                  <MaterialCommunityIcons name="content-duplicate" size={18} color="#FFF" />
                  <Text style={st.aiGenBtnTxt}>Clone Website</Text>
                </>
              )}
            </TouchableOpacity>

            <View style={{ marginTop: 24, padding: 14, backgroundColor: COLORS.bgAlt, borderRadius: 10 }}>
              <Text style={{ fontSize: 12, fontWeight: '700', color: COLORS.text, marginBottom: 8 }}>What gets cloned</Text>
              <Text style={st.cloneInfoLine}>✓ Page structure (Home, About, Services, Contact)</Text>
              <Text style={st.cloneInfoLine}>✓ Section layout and ordering</Text>
              <Text style={st.cloneInfoLine}>✓ Content text where publicly readable</Text>
              <Text style={st.cloneInfoLine}>✓ Color hints from the original</Text>
              <Text style={[st.cloneInfoLine, { marginTop: 8, color: COLORS.sub }]}>✗ Images, logos, copyrighted materials — bring your own</Text>
              <Text style={[st.cloneInfoLine, { color: COLORS.sub }]}>✗ Backend logic, custom code, integrations</Text>
            </View>
          </ScrollView>
        </SafeAreaView>
      </KeyboardAvoidingView>
    </Modal>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // Modal: Website Builder (inline) — full editor
  // ══════════════════════════════════════════════════════════════════════════
  const renderWebsiteBuilderModal = () => {
    if (!editingWebsite) return null;
    const pages = Array.isArray(editingWebsite.pages) ? editingWebsite.pages : [];
    const themeColor = editingWebsite.theme_color || COLORS.primary;

    return (
      <Modal visible={showWebsiteBuilder} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => {
        if (websiteDirty) {
          Alert.alert(
            'Unsaved changes',
            'You have unsaved changes. Save before closing?',
            [
              { text: 'Discard', style: 'destructive', onPress: () => { setShowWebsiteBuilder(false); setWebsiteDirty(false); } },
              { text: 'Cancel', style: 'cancel' },
              { text: 'Save', onPress: async () => { await saveWebsiteChanges(); setShowWebsiteBuilder(false); } },
            ]
          );
        } else {
          setShowWebsiteBuilder(false);
        }
      }}>
        <SafeAreaView style={st.modal}>
          <View style={st.modalH}>
            <TouchableOpacity onPress={() => {
              if (websiteDirty) {
                Alert.alert('Unsaved changes', 'Save before closing?', [
                  { text: 'Discard', style: 'destructive', onPress: () => { setShowWebsiteBuilder(false); setWebsiteDirty(false); } },
                  { text: 'Cancel', style: 'cancel' },
                  { text: 'Save', onPress: async () => { await saveWebsiteChanges(); setShowWebsiteBuilder(false); } },
                ]);
              } else {
                setShowWebsiteBuilder(false);
              }
            }}>
              <MaterialCommunityIcons name="close" size={26} color={COLORS.text} />
            </TouchableOpacity>
            <View style={{ flex: 1, marginHorizontal: 12 }}>
              <Text style={st.modalTitle} numberOfLines={1}>{editingWebsite.name}</Text>
              {websiteDirty && <Text style={{ fontSize: 11, color: COLORS.warning, fontWeight: '600' }}>● Unsaved changes</Text>}
            </View>
            <TouchableOpacity onPress={saveWebsiteChanges} disabled={!websiteDirty || savingWebsite}>
              {savingWebsite
                ? <ActivityIndicator color={themeColor} />
                : <Text style={[st.saveBtn, { color: websiteDirty ? themeColor : COLORS.light }]}>Save</Text>}
            </TouchableOpacity>
          </View>

          <ScrollView style={{ flex: 1 }} keyboardShouldPersistTaps="handled">
            {/* Hero card with theme color */}
            <LinearGradient colors={[themeColor, `${themeColor}CC`]} style={st.builderHero}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <MaterialCommunityIcons name="web" size={32} color="#FFF" />
                <View style={{ flex: 1, marginLeft: 12 }}>
                  <Text style={st.builderHeroTitle}>{editingWebsite.name}</Text>
                  <Text style={st.builderHeroSub}>{pages.length} pages · {editingWebsite.status || 'draft'}</Text>
                </View>
                {activeWebsiteId === editingWebsite.id && (
                  <View style={st.activeChip}><Text style={st.activeChipTxt}>ACTIVE</Text></View>
                )}
              </View>
            </LinearGradient>

            {/* Website metadata edit */}
            <View style={{ padding: 16 }}>
              <Text style={st.anaSection}>Website Details</Text>
              <View style={st.formField}>
                <Text style={st.formLbl}>Name</Text>
                <TextInput
                  style={st.formInput}
                  value={editingWebsite.name || ''}
                  onChangeText={(v) => updateWebsiteField('name', v)}
                  placeholder="Site name"
                  placeholderTextColor={COLORS.light}
                />
              </View>
              <View style={st.formField}>
                <Text style={st.formLbl}>Domain</Text>
                <TextInput
                  style={st.formInput}
                  value={editingWebsite.domain || ''}
                  onChangeText={(v) => updateWebsiteField('domain', v)}
                  placeholder="example.com"
                  autoCapitalize="none"
                  autoCorrect={false}
                  placeholderTextColor={COLORS.light}
                />
              </View>
              <View style={st.formField}>
                <Text style={st.formLbl}>Status</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  {['draft', 'published', 'archived'].map(s => (
                    <TouchableOpacity
                      key={s}
                      style={[st.selectChip, editingWebsite.status === s && { backgroundColor: themeColor, borderColor: themeColor }]}
                      onPress={() => updateWebsiteField('status', s)}
                    >
                      <Text style={[st.selectChipTxt, editingWebsite.status === s && { color: '#FFF', fontWeight: '700' }]}>{s}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>

              {/* Pages section */}
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                <Text style={[st.anaSection, { marginTop: 8, marginBottom: 0 }]}>Pages ({pages.length})</Text>
                <TouchableOpacity style={[st.smallBtn, { backgroundColor: themeColor }]} onPress={addPage}>
                  <MaterialCommunityIcons name="plus" size={14} color="#FFF" />
                  <Text style={st.smallBtnTxt}>Add Page</Text>
                </TouchableOpacity>
              </View>

              {pages.length === 0 ? (
                <View style={[st.empty, { padding: 24 }]}>
                  <MaterialCommunityIcons name="file-plus-outline" size={48} color={COLORS.border} />
                  <Text style={[st.emptySub, { marginTop: 8 }]}>No pages yet. Add your first page.</Text>
                </View>
              ) : (
                pages.map((page, idx) => (
                  <View key={idx} style={st.editablePageRow}>
                    <TouchableOpacity style={{ flexDirection: 'row', flex: 1, alignItems: 'center' }} onPress={() => openPageEditor(page, idx)}>
                      <View style={[st.pageIcon, { backgroundColor: themeColor }]}>
                        <Text style={{ color: '#FFF', fontWeight: '700', fontSize: 12 }}>{idx + 1}</Text>
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={st.pageTitle}>{page.title}</Text>
                        <Text style={st.pageSlug}>
                          /{page.slug}
                          {page.is_published ? '  ●  Published' : '  ●  Draft'}
                          {page.content ? `  ·  ${String(page.content).length} chars` : '  ·  Empty'}
                        </Text>
                      </View>
                    </TouchableOpacity>
                    <View style={st.pageActions}>
                      <TouchableOpacity style={st.pageActionBtn} onPress={() => movePage(idx, -1)} disabled={idx === 0}>
                        <MaterialCommunityIcons name="arrow-up" size={16} color={idx === 0 ? COLORS.border : COLORS.sub} />
                      </TouchableOpacity>
                      <TouchableOpacity style={st.pageActionBtn} onPress={() => movePage(idx, 1)} disabled={idx === pages.length - 1}>
                        <MaterialCommunityIcons name="arrow-down" size={16} color={idx === pages.length - 1 ? COLORS.border : COLORS.sub} />
                      </TouchableOpacity>
                      <TouchableOpacity style={st.pageActionBtn} onPress={() => duplicatePage(idx)}>
                        <MaterialCommunityIcons name="content-copy" size={16} color={COLORS.sub} />
                      </TouchableOpacity>
                      <TouchableOpacity style={st.pageActionBtn} onPress={() => deletePage(idx)}>
                        <MaterialCommunityIcons name="delete-outline" size={16} color={COLORS.danger} />
                      </TouchableOpacity>
                    </View>
                  </View>
                ))
              )}

              {/* SEO + Social section */}
              <Text style={[st.anaSection, { marginTop: 20 }]}>SEO & Social</Text>
              <TouchableOpacity style={st.linkRow} onPress={() => setShowSeoEditor(true)}>
                <MaterialCommunityIcons name="magnify-scan" size={20} color={themeColor} />
                <View style={{ flex: 1, marginLeft: 12 }}>
                  <Text style={st.linkRowTitle}>Edit SEO Settings</Text>
                  <Text style={st.linkRowSub}>Meta title, description, keywords, social cards</Text>
                </View>
                <MaterialCommunityIcons name="chevron-right" size={20} color={COLORS.light} />
              </TouchableOpacity>

              {/* Action buttons */}
              <View style={{ flexDirection: 'row', gap: 10, marginTop: 24 }}>
                <TouchableOpacity
                  style={[st.detailActBtn, { flex: 1, backgroundColor: themeColor, paddingVertical: 14 }]}
                  onPress={() => {
                    if (navigation && typeof navigation.navigate === 'function') {
                      try {
                        navigation.navigate('WebsiteManagement', { website: editingWebsite });
                        setShowWebsiteBuilder(false);
                      } catch {
                        Alert.alert('Builder not available', 'Register WebsiteManagement in App.js for the visual builder.');
                      }
                    }
                  }}
                >
                  <MaterialCommunityIcons name="palette" size={16} color="#FFF" />
                  <Text style={st.detailActTxt}>Visual Builder</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[st.detailActBtn, { flex: 1, backgroundColor: COLORS.success, paddingVertical: 14 }]}
                  onPress={async () => {
                    if (websiteDirty) await saveWebsiteChanges();
                    const newStatus = editingWebsite.status === 'published' ? 'draft' : 'published';
                    updateWebsiteField('status', newStatus);
                  }}
                >
                  <MaterialCommunityIcons name={editingWebsite.status === 'published' ? 'cloud-off-outline' : 'rocket-launch'} size={16} color="#FFF" />
                  <Text style={st.detailActTxt}>{editingWebsite.status === 'published' ? 'Unpublish' : 'Publish'}</Text>
                </TouchableOpacity>
              </View>
            </View>

            <View style={{ height: 40 }} />
          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Modal: Page Editor (deep edit per page)
  // ══════════════════════════════════════════════════════════════════════════
  const renderPageEditorModal = () => {
    if (!editingPage) return null;
    const themeColor = editingWebsite?.theme_color || COLORS.primary;

    return (
      <Modal visible={showPageEditor} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowPageEditor(false)}>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <SafeAreaView style={st.modal}>
            <View style={st.modalH}>
              <TouchableOpacity onPress={() => setShowPageEditor(false)}>
                <MaterialCommunityIcons name="close" size={26} color={COLORS.text} />
              </TouchableOpacity>
              <Text style={st.modalTitle} numberOfLines={1}>Edit: {editingPage.title}</Text>
              <TouchableOpacity onPress={savePageEdits}>
                <Text style={[st.saveBtn, { color: themeColor }]}>Done</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={{ flex: 1, padding: 16 }} keyboardShouldPersistTaps="handled">
              <View style={st.formField}>
                <Text style={st.formLbl}>Page Title <Text style={{ color: COLORS.danger }}>*</Text></Text>
                <TextInput
                  style={st.formInput}
                  value={editingPage.title || ''}
                  onChangeText={(v) => updatePageField('title', v)}
                  placeholder="Page title"
                  placeholderTextColor={COLORS.light}
                />
              </View>

              <View style={st.formField}>
                <Text style={st.formLbl}>URL Slug</Text>
                <TextInput
                  style={st.formInput}
                  value={editingPage.slug || ''}
                  onChangeText={(v) => updatePageField('slug', v.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  placeholder="page-url-slug"
                  autoCapitalize="none"
                  autoCorrect={false}
                  placeholderTextColor={COLORS.light}
                />
                <Text style={{ fontSize: 11, color: COLORS.sub, marginTop: 4 }}>
                  Will appear as: {editingWebsite?.domain || 'yoursite.com'}/{editingPage.slug || 'slug'}
                </Text>
              </View>

              <View style={st.formField}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Text style={st.formLbl}>Content</Text>
                  <TouchableOpacity
                    style={[st.smallBtn, { backgroundColor: COLORS.purple }, aiGenerating && { backgroundColor: COLORS.light }]}
                    onPress={generatePageContent}
                    disabled={aiGenerating}
                  >
                    {aiGenerating
                      ? <ActivityIndicator color="#FFF" size="small" />
                      : <>
                          <MaterialCommunityIcons name="sparkles" size={14} color="#FFF" />
                          <Text style={st.smallBtnTxt}>AI Generate</Text>
                        </>}
                  </TouchableOpacity>
                </View>
                <TextInput
                  style={[st.formInput, { minHeight: 220, textAlignVertical: 'top' }]}
                  value={editingPage.content || ''}
                  onChangeText={(v) => updatePageField('content', v)}
                  placeholder={`Write the content for this page. Use Markdown:\n\n# Heading\n## Subheading\n**Bold** *Italic*\n- Bullet list\n[Link](https://example.com)`}
                  placeholderTextColor={COLORS.light}
                  multiline
                />
                <Text style={{ fontSize: 11, color: COLORS.sub, marginTop: 4 }}>
                  {(editingPage.content || '').length} characters · Supports Markdown
                </Text>
              </View>

              <View style={st.formField}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Text style={st.formLbl}>Published</Text>
                  <Switch
                    value={!!editingPage.is_published}
                    onValueChange={(v) => updatePageField('is_published', v)}
                    trackColor={{ false: COLORS.border, true: themeColor }}
                    thumbColor="#FFF"
                  />
                </View>
                <Text style={{ fontSize: 11, color: COLORS.sub, marginTop: 4 }}>
                  {editingPage.is_published
                    ? 'This page will be visible on your live site.'
                    : 'This page is a draft and only visible to you.'}
                </Text>
              </View>

              <Text style={[st.anaSection, { marginTop: 16 }]}>SEO for this Page</Text>

              <View style={st.formField}>
                <Text style={st.formLbl}>Meta Title</Text>
                <TextInput
                  style={st.formInput}
                  value={editingPage.meta_title || ''}
                  onChangeText={(v) => updatePageField('meta_title', v)}
                  placeholder={editingPage.title || 'Shown in search results & browser tab'}
                  placeholderTextColor={COLORS.light}
                  maxLength={60}
                />
                <Text style={{ fontSize: 11, color: COLORS.sub, marginTop: 4 }}>
                  {(editingPage.meta_title || '').length} / 60 — keep under 60 for best results
                </Text>
              </View>

              <View style={st.formField}>
                <Text style={st.formLbl}>Meta Description</Text>
                <TextInput
                  style={[st.formInput, { minHeight: 80, textAlignVertical: 'top' }]}
                  value={editingPage.meta_description || ''}
                  onChangeText={(v) => updatePageField('meta_description', v)}
                  placeholder="Short description shown in Google results"
                  placeholderTextColor={COLORS.light}
                  multiline
                  maxLength={160}
                />
                <Text style={{ fontSize: 11, color: COLORS.sub, marginTop: 4 }}>
                  {(editingPage.meta_description || '').length} / 160
                </Text>
              </View>

              <View style={{ height: 32 }} />
            </ScrollView>
          </SafeAreaView>
        </KeyboardAvoidingView>
      </Modal>
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Modal: SEO Editor (website-level)
  // ══════════════════════════════════════════════════════════════════════════
  const renderSeoEditorModal = () => {
    if (!editingWebsite) return null;
    const themeColor = editingWebsite.theme_color || COLORS.primary;

    return (
      <Modal visible={showSeoEditor} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowSeoEditor(false)}>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <SafeAreaView style={st.modal}>
            <View style={st.modalH}>
              <TouchableOpacity onPress={() => setShowSeoEditor(false)}>
                <MaterialCommunityIcons name="arrow-left" size={26} color={COLORS.text} />
              </TouchableOpacity>
              <Text style={st.modalTitle}>SEO & Social</Text>
              <TouchableOpacity onPress={() => setShowSeoEditor(false)}>
                <Text style={[st.saveBtn, { color: themeColor }]}>Done</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={{ flex: 1, padding: 16 }} keyboardShouldPersistTaps="handled">
              <View style={[st.builderBanner, { backgroundColor: `${themeColor}15` }]}>
                <MaterialCommunityIcons name="information-outline" size={20} color={themeColor} />
                <Text style={{ flex: 1, marginLeft: 10, fontSize: 12, color: COLORS.text, lineHeight: 17 }}>
                  These settings apply across the whole site as defaults. Individual pages can override them.
                </Text>
              </View>

              <Text style={st.anaSection}>Site SEO</Text>

              <View style={st.formField}>
                <Text style={st.formLbl}>Site Title</Text>
                <TextInput
                  style={st.formInput}
                  value={editingWebsite.seo_title || ''}
                  onChangeText={(v) => updateWebsiteField('seo_title', v)}
                  placeholder={editingWebsite.name}
                  placeholderTextColor={COLORS.light}
                  maxLength={60}
                />
                <Text style={{ fontSize: 11, color: COLORS.sub, marginTop: 4 }}>{(editingWebsite.seo_title || '').length} / 60</Text>
              </View>

              <View style={st.formField}>
                <Text style={st.formLbl}>Site Description</Text>
                <TextInput
                  style={[st.formInput, { minHeight: 80, textAlignVertical: 'top' }]}
                  value={editingWebsite.seo_description || ''}
                  onChangeText={(v) => updateWebsiteField('seo_description', v)}
                  placeholder="What is this website about? (160 characters max)"
                  placeholderTextColor={COLORS.light}
                  multiline
                  maxLength={160}
                />
                <Text style={{ fontSize: 11, color: COLORS.sub, marginTop: 4 }}>{(editingWebsite.seo_description || '').length} / 160</Text>
              </View>

              <View style={st.formField}>
                <Text style={st.formLbl}>Keywords (comma-separated)</Text>
                <TextInput
                  style={st.formInput}
                  value={editingWebsite.seo_keywords || ''}
                  onChangeText={(v) => updateWebsiteField('seo_keywords', v)}
                  placeholder="construction, builders, london"
                  autoCapitalize="none"
                  placeholderTextColor={COLORS.light}
                />
              </View>

              <Text style={[st.anaSection, { marginTop: 8 }]}>Social Sharing</Text>

              <View style={st.formField}>
                <Text style={st.formLbl}>Social Image URL</Text>
                <TextInput
                  style={st.formInput}
                  value={editingWebsite.og_image || ''}
                  onChangeText={(v) => updateWebsiteField('og_image', v)}
                  placeholder="https://example.com/share-image.jpg"
                  autoCapitalize="none"
                  placeholderTextColor={COLORS.light}
                />
                <Text style={{ fontSize: 11, color: COLORS.sub, marginTop: 4 }}>
                  Shown when your site is shared on Facebook, Twitter, LinkedIn. Recommended: 1200×630px.
                </Text>
              </View>

              <View style={st.formField}>
                <Text style={st.formLbl}>Twitter Handle</Text>
                <TextInput
                  style={st.formInput}
                  value={editingWebsite.twitter_handle || ''}
                  onChangeText={(v) => updateWebsiteField('twitter_handle', v.replace(/^@/, ''))}
                  placeholder="yourcompany"
                  autoCapitalize="none"
                  placeholderTextColor={COLORS.light}
                />
              </View>

              <Text style={[st.anaSection, { marginTop: 8 }]}>Advanced</Text>

              <View style={st.formField}>
                <Text style={st.formLbl}>Google Analytics ID</Text>
                <TextInput
                  style={st.formInput}
                  value={editingWebsite.ga_id || ''}
                  onChangeText={(v) => updateWebsiteField('ga_id', v)}
                  placeholder="G-XXXXXXXXXX"
                  autoCapitalize="characters"
                  placeholderTextColor={COLORS.light}
                />
              </View>

              <View style={st.formField}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <View style={{ flex: 1 }}>
                    <Text style={st.formLbl}>Index in Search Engines</Text>
                    <Text style={{ fontSize: 11, color: COLORS.sub, marginTop: 2 }}>
                      Allow Google and others to find this site
                    </Text>
                  </View>
                  <Switch
                    value={editingWebsite.allow_indexing !== false}
                    onValueChange={(v) => updateWebsiteField('allow_indexing', v)}
                    trackColor={{ false: COLORS.border, true: themeColor }}
                    thumbColor="#FFF"
                  />
                </View>
              </View>

              <View style={{ height: 32 }} />
            </ScrollView>
          </SafeAreaView>
        </KeyboardAvoidingView>
      </Modal>
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Modal: Publish content to a website (picker)
  // ══════════════════════════════════════════════════════════════════════════
  const renderPublishModal = () => (
    <Modal visible={showPublishModal} animationType="fade" transparent onRequestClose={() => setShowPublishModal(false)}>
      <Pressable style={st.filterBackdrop} onPress={() => setShowPublishModal(false)}>
        <Pressable style={st.filterSheet}>
          <Text style={st.modalTitle}>Publish to Website</Text>
          <Text style={{ fontSize: 12, color: COLORS.sub, marginTop: 4 }}>
            Choose where to post "{publishTarget?.title || publishTarget?.name || 'this item'}"
          </Text>
          <ScrollView style={{ marginTop: 12, maxHeight: 400 }}>
            {websites.map(w => {
              const tmpl = WEBSITE_TEMPLATES.find(t => t.id === w.template_id) || { color: '#06B6D4', icon: 'web' };
              return (
                <TouchableOpacity
                  key={w.id}
                  style={st.publishOption}
                  onPress={() => publishContentToWebsite(w.id)}
                >
                  <View style={[st.publishIcon, { backgroundColor: tmpl.color }]}>
                    <MaterialCommunityIcons name={tmpl.icon} size={18} color="#FFF" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={st.publishName}>{w.name}</Text>
                    <Text style={st.publishMeta}>
                      {w.domain || w.source_url || 'No domain'} · {(w.pages || []).length} pages
                    </Text>
                  </View>
                  {activeWebsiteId === w.id && (
                    <View style={st.activeChipSmall}>
                      <Text style={st.activeChipSmallTxt}>ACTIVE</Text>
                    </View>
                  )}
                  <MaterialCommunityIcons name="chevron-right" size={18} color={COLORS.light} />
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // Render — Final assembly
  // ══════════════════════════════════════════════════════════════════════════
  if (isLoading) {
    return (
      <SafeAreaView style={st.loading}>
        <StatusBar barStyle="dark-content" />
        <ActivityIndicator size="large" color={COLORS.accent} />
        <Text style={st.loadingTxt}>Loading content...</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={st.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.accent} />
      {renderHeader()}
      {renderStats()}
      {renderTabs()}
      {renderToolbar()}
      <ScrollView
        style={{ flex: 1 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[activeTabMeta.color]} />}
        showsVerticalScrollIndicator={false}
      >
        {renderList()}
        <View style={{ height: 100 }} />
      </ScrollView>

      {/* Floating Action Buttons */}
      <View style={st.fabContainer}>
        {activeTab === 'websites' ? (
          <>
            <TouchableOpacity style={[st.fabSecondary, { backgroundColor: COLORS.purple }]} onPress={() => setShowWebsiteClone(true)}>
              <MaterialCommunityIcons name="content-duplicate" size={20} color="#FFF" />
            </TouchableOpacity>
            <TouchableOpacity style={[st.fab, { backgroundColor: '#06B6D4' }]} onPress={() => setShowWebsiteCreate(true)}>
              <MaterialCommunityIcons name="plus" size={28} color="#FFF" />
            </TouchableOpacity>
          </>
        ) : (
          <>
            <TouchableOpacity style={[st.fabSecondary, { backgroundColor: COLORS.purple }]} onPress={() => setShowAIModal(true)}>
              <MaterialCommunityIcons name="sparkles" size={22} color="#FFF" />
            </TouchableOpacity>
            <TouchableOpacity style={[st.fab, { backgroundColor: activeTabMeta.color }]} onPress={() => openCreate()}>
              <MaterialCommunityIcons name="plus" size={28} color="#FFF" />
            </TouchableOpacity>
          </>
        )}
      </View>

      {renderCreateModal()}
      {renderDetailModal()}
      {renderAIModal()}
      {renderFilterModal()}
      {renderBulkModal()}
      {renderAnalyticsModal()}
      {renderWebsiteCreateModal()}
      {renderWebsiteCloneModal()}
      {renderWebsiteBuilderModal()}
      {renderPageEditorModal()}
      {renderSeoEditorModal()}
      {renderPublishModal()}
    </SafeAreaView>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Styles
// ══════════════════════════════════════════════════════════════════════════════
const st = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.bg },
  loading: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: COLORS.bg },
  loadingTxt: { fontSize: 16, color: COLORS.sub, marginTop: 16 },

  // Header
  header: { paddingTop: 8, paddingBottom: 16, paddingHorizontal: 16 },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  hBtn: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.12)', justifyContent: 'center', alignItems: 'center' },
  hTitle: { fontSize: 20, fontWeight: 'bold', color: '#FFF' },
  hSub: { fontSize: 13, marginTop: 2, color: '#FED7AA' },

  // Stats
  statsBar: { flexGrow: 0, flexShrink: 0, backgroundColor: COLORS.surface, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: COLORS.border },
  statCard: { alignItems: 'center', marginRight: 16, minWidth: 70 },
  statIconWrap: { width: 36, height: 36, borderRadius: 18, justifyContent: 'center', alignItems: 'center', marginBottom: 4 },
  statVal: { fontSize: 16, fontWeight: 'bold' },
  statLbl: { fontSize: 11, color: COLORS.sub, marginTop: 2 },

  // Tabs
  tabsRow: { backgroundColor: COLORS.surface, borderBottomWidth: 1, borderBottomColor: COLORS.border, paddingVertical: 4 },
  tab: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 10, marginRight: 4, borderRadius: 20 },
  tabTxt: { fontSize: 13, color: COLORS.sub, marginLeft: 6, fontWeight: '500' },

  // Toolbar
  toolbar: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingTop: 12, paddingBottom: 8, backgroundColor: COLORS.surface, gap: 8 },
  searchRow: { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.bg, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8 },
  searchInput: { flex: 1, fontSize: 14, color: COLORS.text, marginLeft: 8 },
  iconBtn: { width: 40, height: 40, borderRadius: 10, backgroundColor: COLORS.bg, justifyContent: 'center', alignItems: 'center' },
  iconBtnBadge: { fontSize: 14, fontWeight: '700' },

  // Card
  card: { backgroundColor: COLORS.surface, marginHorizontal: 12, marginVertical: 4, padding: 14, borderRadius: 12, borderWidth: 1, borderColor: COLORS.border },
  cardSelected: { backgroundColor: '#EFF6FF', borderColor: COLORS.primary },
  cardRow: { flexDirection: 'row', alignItems: 'center' },
  avatar: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  cardName: { fontSize: 15, fontWeight: '700', color: COLORS.text },
  cardMeta: { fontSize: 12, color: COLORS.sub, marginTop: 2, lineHeight: 16 },
  cardDate: { fontSize: 11, color: COLORS.light, marginLeft: 6 },
  cardVal: { fontSize: 14, fontWeight: '700', color: COLORS.success },
  badge: { flexDirection: 'row', alignItems: 'center', borderRadius: 10, paddingHorizontal: 8, paddingVertical: 2, marginTop: 4 },
  badgeTxt: { fontSize: 10, fontWeight: '700', textTransform: 'capitalize' },
  checkbox: { width: 22, height: 22, borderRadius: 11, borderWidth: 2, borderColor: COLORS.border, marginRight: 10, justifyContent: 'center', alignItems: 'center' },

  // Empty
  empty: { alignItems: 'center', padding: 40, marginTop: 20 },
  emptyTitle: { fontSize: 17, fontWeight: '700', color: COLORS.text, marginTop: 12 },
  emptySub: { fontSize: 13, color: COLORS.sub, marginTop: 4, textAlign: 'center' },
  emptyBtn: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 10, gap: 6 },
  emptyBtnTxt: { color: '#FFF', fontWeight: '700', fontSize: 13 },
  moreNote: { fontSize: 12, color: COLORS.sub, textAlign: 'center', padding: 16, fontStyle: 'italic' },

  // FAB
  fabContainer: { position: 'absolute', bottom: 20, right: 16, flexDirection: 'column', alignItems: 'center', gap: 10 },
  fab: { width: 56, height: 56, borderRadius: 28, justifyContent: 'center', alignItems: 'center', elevation: 4, shadowColor: '#000', shadowOpacity: 0.2, shadowOffset: { width: 0, height: 2 }, shadowRadius: 4 },
  fabSecondary: { width: 44, height: 44, borderRadius: 22, justifyContent: 'center', alignItems: 'center', elevation: 3 },

  // Modal
  modal: { flex: 1, backgroundColor: COLORS.surface },
  modalH: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: COLORS.border },
  modalTitle: { fontSize: 17, fontWeight: '700', color: COLORS.text, flex: 1, marginHorizontal: 12 },
  saveBtn: { fontSize: 16, fontWeight: '700' },

  // Form
  formField: { marginBottom: 16 },
  formLbl: { fontSize: 13, fontWeight: '700', color: COLORS.text, marginBottom: 6 },
  formInput: { backgroundColor: COLORS.bgAlt, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 12, fontSize: 14, color: COLORS.text, borderWidth: 1, borderColor: COLORS.border },
  selectChip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 18, borderWidth: 1, borderColor: COLORS.border, marginRight: 8, backgroundColor: COLORS.bgAlt },
  selectChipTxt: { fontSize: 13, color: COLORS.sub, fontWeight: '500' },

  // AI assist
  aiAssistBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', padding: 12, borderRadius: 10, marginTop: 8, gap: 6 },
  aiAssistTxt: { fontSize: 14, fontWeight: '700' },
  aiGenBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: COLORS.purple, padding: 14, borderRadius: 12, marginTop: 16, gap: 6 },
  aiGenBtnTxt: { color: '#FFF', fontSize: 15, fontWeight: '700' },
  aiResultBox: { backgroundColor: COLORS.bgAlt, borderRadius: 10, padding: 14, borderWidth: 1, borderColor: COLORS.border, marginTop: 6 },
  aiResultText: { fontSize: 13, color: COLORS.text, lineHeight: 20 },
  aiUseBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', padding: 12, borderRadius: 10, marginTop: 12, gap: 6 },
  aiUseBtnTxt: { color: '#FFF', fontSize: 14, fontWeight: '700' },

  // Detail
  detailActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 20 },
  detailActBtn: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, gap: 4 },
  detailActTxt: { color: '#FFF', fontSize: 12, fontWeight: '700' },
  detailRow: { paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: COLORS.border },
  detailKey: { fontSize: 11, color: COLORS.sub, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  detailVal: { fontSize: 14, color: COLORS.text, lineHeight: 20 },

  // Filter sheet
  filterBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  filterSheet: { backgroundColor: COLORS.surface, padding: 20, borderTopLeftRadius: 22, borderTopRightRadius: 22, paddingBottom: 32 },
  filterOption: { flexDirection: 'row', alignItems: 'center', padding: 12, borderRadius: 10, gap: 10 },
  filterOptionTxt: { fontSize: 15, color: COLORS.text, fontWeight: '500' },

  // Bulk
  bulkAction: { flexDirection: 'row', alignItems: 'center', padding: 14, borderRadius: 10, gap: 10 },
  bulkActionTxt: { fontSize: 15, color: COLORS.text, fontWeight: '600' },

  // Analytics
  anaSection: { fontSize: 14, fontWeight: '700', color: COLORS.text, marginTop: 8, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' },
  gridCard: { width: (SCREEN_W - 48) / 2, backgroundColor: COLORS.bgAlt, borderRadius: 12, padding: 16, alignItems: 'center', marginBottom: 10 },
  gridVal: { fontSize: 24, fontWeight: 'bold' },
  gridLbl: { fontSize: 12, color: COLORS.sub, marginTop: 4 },
  barRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  barLbl: { fontSize: 13, color: COLORS.text, width: 100, textTransform: 'capitalize' },
  barBg: { flex: 1, height: 8, backgroundColor: COLORS.border, borderRadius: 4, marginHorizontal: 8, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 4 },
  barCnt: { fontSize: 13, fontWeight: '700', color: COLORS.text, width: 30, textAlign: 'right' },
  totalCard: { backgroundColor: COLORS.bgAlt, borderRadius: 12, padding: 24, alignItems: 'center' },
  totalNum: { fontSize: 36, fontWeight: 'bold', color: COLORS.accent },
  totalLbl: { fontSize: 13, color: COLORS.sub, marginTop: 4 },

  // Website cards
  websiteCard: { backgroundColor: COLORS.surface, marginHorizontal: 12, marginVertical: 6, borderRadius: 14, borderWidth: 1, borderColor: COLORS.border, overflow: 'hidden' },
  websiteHeader: { padding: 14 },
  websiteName: { fontSize: 16, fontWeight: '700', color: '#FFF' },
  websiteDomain: { fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 2 },
  activeChip: { backgroundColor: 'rgba(255,255,255,0.25)', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 },
  activeChipTxt: { color: '#FFF', fontSize: 10, fontWeight: '800', letterSpacing: 0.5 },
  websiteMeta: { flexDirection: 'row', flexWrap: 'wrap', padding: 12, gap: 12, backgroundColor: COLORS.bgAlt },
  websiteMetaItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  websiteMetaTxt: { fontSize: 12, color: COLORS.sub, fontWeight: '500' },
  statusDot: { width: 8, height: 8, borderRadius: 4 },
  websiteActions: { flexDirection: 'row', padding: 10, gap: 8 },
  websiteBtn: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, gap: 4 },
  websiteBtnTxt: { color: '#FFF', fontSize: 12, fontWeight: '700' },

  // Template picker
  templateCard: { backgroundColor: COLORS.surface, padding: 14, borderRadius: 12, borderWidth: 1, borderColor: COLORS.border, marginBottom: 10, flexDirection: 'row', alignItems: 'center' },
  templateIconBox: { width: 56, height: 56, borderRadius: 12, justifyContent: 'center', alignItems: 'center', marginRight: 14 },
  templateName: { fontSize: 16, fontWeight: '700', color: COLORS.text },
  templateDesc: { fontSize: 12, color: COLORS.sub, marginTop: 2, lineHeight: 16 },
  templateSections: { fontSize: 11, color: COLORS.light, marginTop: 4, fontWeight: '600' },

  // Clone info
  cloneInfoLine: { fontSize: 12, color: COLORS.text, lineHeight: 20 },

  // Builder
  builderBanner: { flexDirection: 'row', padding: 12, borderRadius: 10, marginBottom: 16, alignItems: 'flex-start' },
  pageRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.surface, padding: 12, borderRadius: 10, borderWidth: 1, borderColor: COLORS.border, marginBottom: 6 },
  pageIcon: { width: 28, height: 28, borderRadius: 14, justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  pageTitle: { fontSize: 14, fontWeight: '600', color: COLORS.text },
  pageSlug: { fontSize: 11, color: COLORS.sub, marginTop: 2 },

  // Publish modal
  publishOption: { flexDirection: 'row', alignItems: 'center', padding: 12, borderRadius: 10, marginBottom: 6, backgroundColor: COLORS.bgAlt },
  publishIcon: { width: 36, height: 36, borderRadius: 18, justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  publishName: { fontSize: 14, fontWeight: '700', color: COLORS.text },
  publishMeta: { fontSize: 11, color: COLORS.sub, marginTop: 2 },
  activeChipSmall: { backgroundColor: '#06B6D4', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8, marginRight: 8 },
  activeChipSmallTxt: { color: '#FFF', fontSize: 9, fontWeight: '800', letterSpacing: 0.5 },

  // Builder editor
  builderHero: { padding: 20 },
  builderHeroTitle: { fontSize: 18, fontWeight: '700', color: '#FFF' },
  builderHeroSub: { fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 2 },
  smallBtn: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 14, gap: 4 },
  smallBtnTxt: { color: '#FFF', fontSize: 12, fontWeight: '700' },
  editablePageRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.surface, padding: 10, borderRadius: 10, borderWidth: 1, borderColor: COLORS.border, marginBottom: 6 },
  pageActions: { flexDirection: 'row', alignItems: 'center' },
  pageActionBtn: { width: 30, height: 30, borderRadius: 15, justifyContent: 'center', alignItems: 'center' },
  linkRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.bgAlt, padding: 14, borderRadius: 10, borderWidth: 1, borderColor: COLORS.border, marginBottom: 8 },
  linkRowTitle: { fontSize: 14, fontWeight: '700', color: COLORS.text },
  linkRowSub: { fontSize: 12, color: COLORS.sub, marginTop: 2 },
});
